import React, { useEffect, useState, useContext, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ChatbotAnalyticsTab } from '@/components/admin/ChatbotAnalyticsTab';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import ImageUpload from '@/components/ImageUpload';
import { APIProvider } from '@vis.gl/react-google-maps';
import LocationMapPicker from '@/components/LocationMapPicker';
import KoryuScrollBanner from '@/components/KoryuScrollBanner';
import ExchangeRateDisplay from '@/components/ExchangeRateDisplay';
import MessagesPanel from '@/components/MessagesPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const AdminDashboard = () => {
  const { user, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [schools, setSchools] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Tab state management
  const [activeTab, setActiveTab] = useState("pending-courses");
  const [activeSchoolTab, setActiveSchoolTab] = useState("locations");
  // Analytics States
  const [analyticsData, setAnalyticsData] = useState(null);
  const [realtimeData, setRealtimeData] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [analyticsDays, setAnalyticsDays] = useState(30);

  // Payout Management States
  const [payoutSummary, setPayoutSummary] = useState(null);
  const [payoutLoading, setPayoutLoading] = useState(false);
  const [payoutDialog, setPayoutDialog] = useState(null);
  const [payoutForm, setPayoutForm] = useState({ payout_method: '', transaction_reference: '', admin_notes: '' });
  // AI Knowledge Base States
  const [knowledgeArticles, setKnowledgeArticles] = useState([]);
  const [knowledgeLoading, setKnowledgeLoading] = useState(false);
  const [newArticle, setNewArticle] = useState({ title: '', content: '', category: 'general', source_url: '' });
  const [editingArticle, setEditingArticle] = useState(null);
  const [showKnowledgeForm, setShowKnowledgeForm] = useState(false);
  // Google Drive Sync States
  const [driveStatus, setDriveStatus] = useState(null);
  const [driveSyncing, setDriveSyncing] = useState(false);
  const [driveFiles, setDriveFiles] = useState(null);
  const [funnelData, setFunnelData] = useState(null);
  const [behaviorReport, setBehaviorReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [chatbotAnalytics, setChatbotAnalytics] = useState(null);
  const [chatbotAnalyticsLoading, setChatbotAnalyticsLoading] = useState(false);
  const [stripeBalance, setStripeBalance] = useState(null);
  const [processingPayout, setProcessingPayout] = useState(null);


  // Admin School Management States
  const [adminSchool, setAdminSchool] = useState(null);
  const [adminLocations, setAdminLocations] = useState([]);
  const [adminInstructors, setAdminInstructors] = useState([]);
  const [adminPrograms, setAdminPrograms] = useState([]);
  const [adminBookings, setAdminBookings] = useState([]);
  
  // Dialog states for admin school
  const [locationDialogOpen, setLocationDialogOpen] = useState(false);
  const [instructorDialogOpen, setInstructorDialogOpen] = useState(false);
  const [programDialogOpen, setProgramDialogOpen] = useState(false);
  const [editingProgram, setEditingProgram] = useState(null);
  
  // Form states for admin school
  const [locationForm, setLocationForm] = useState({
    name: '', address: '', city: '', prefecture: '', capacity: '', facilities: [], 
    facility_images: [], google_maps_url: '', description: '', latitude: null, longitude: null
  });
  const [instructorForm, setInstructorForm] = useState({
    name: '', email: '', phone: '', rank: '', years_experience: '', bio: '', specialties: ''
  });
  const [programForm, setProgramForm] = useState({
    location_id: '', instructor_id: '', title: '', description: '', martial_arts_style: 'Aikido', custom_martial_art: '', is_koryu: false,
    course_category: 'Martial Arts', category: 'Martial Arts', experience_level: 'beginner', 
    class_type: 'group', price: '', currency: 'JPY', capacity: '', prerequisites: '', 
    start_date: '', end_date: '', daily_start_time: '09:00', daily_end_time: '17:00', image_url: ''
  });

  // User Management States
  const [users, setUsers] = useState([]);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userForm, setUserForm] = useState({
    name: '', email: '', password: '', role: 'student', phone: '', school_id: '', instructor_id: ''
  });

  // Branding Management States
  const [brandingData, setBrandingData] = useState({
    site_name: 'Train In Japan',
    site_logo_url: '',
    primary_color: '#059669',
    secondary_color: '#047857',
    favicon_url: '',
    hero_title: 'A Pathway to Martial Excellence',
    hero_subtitle: 'Embark on a Journey of Cultural Enrichment',
    hero_image_url: '',
    about_text: '',
    footer_text: ''
  });
  const [uploadingBranding, setUploadingBranding] = useState(false);

  // Edit Dialog States
  const [editSchoolDialog, setEditSchoolDialog] = useState(false);
  const [editLocationDialog, setEditLocationDialog] = useState(false);
  const [editInstructorDialog, setEditInstructorDialog] = useState(false);
  const [editProgramDialog, setEditProgramDialog] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editingInstructor, setEditingInstructor] = useState(null);

  // Facebook Posting States
  const [facebookPosting, setFacebookPosting] = useState(false);
  const [facebookPostsLog, setFacebookPostsLog] = useState([]);

  // Edit Program Form State (for All Programs edit dialog)
  const [editProgramForm, setEditProgramForm] = useState({
    title: '', description: '', martial_arts_style: '', category: '', 
    experience_level: '', price: '', capacity: '', image_url: '',
    start_date: '', end_date: '', daily_start_time: '09:00', daily_end_time: '17:00',
    location_id: '', instructor_id: '', class_type: 'group', currency: 'JPY',
    course_category: 'Martial Arts', prerequisites: ''
  });

  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const prevUnreadCountRef = useRef(0);

  useEffect(() => {
    fetchAdminData();
    fetchAdminSchoolData();
    fetchUsers();
    fetchBranding();
    fetchNotifications();
  }, []);

  useEffect(() => {
    if (user && user.role === 'admin' && activeTab === 'knowledge') {
      fetchKnowledge();
      fetchDriveStatus();
    }
    if (user && user.role === 'admin' && activeTab === 'financial') {
      fetchPayoutSummary();
      fetchStripeBalance();
    }
    if (user && user.role === 'admin' && activeTab === 'analytics') {
      fetchFunnel();
      fetchAnalytics(analyticsDays);
      fetchRealtimeAnalytics();
      const interval = setInterval(fetchRealtimeAnalytics, 30000); // Update every 30 seconds
      return () => clearInterval(interval);
    }
  }, [user, activeTab, analyticsDays]);



  const fetchAdminData = async () => {
    try {
      console.log('Fetching admin data...');
      const [statsRes, schoolsRes, programsRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/schools`, { withCredentials: true }),
        axios.get(`${API}/admin/programs`, { withCredentials: true })
      ]);
      
      console.log('Admin data fetched:', {
        stats: statsRes.data,
        schools: schoolsRes.data.length,
        programs: programsRes.data.length
      });
      
      setStats(statsRes.data);
      setSchools(schoolsRes.data);
      setPrograms(programsRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      console.error('Error details:', error.response?.data);
      toast.error(`Failed to load admin data: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchAdminSchoolData = async () => {
    try {
      console.log('Fetching admin school data...');
      const schoolRes = await axios.get(`${API}/schools/my/school`, { withCredentials: true });
      setAdminSchool(schoolRes.data);
      console.log('Admin school fetched:', schoolRes.data);

      const [locsRes, instsRes, progsRes, booksRes] = await Promise.all([
        axios.get(`${API}/locations?school_id=${schoolRes.data.id}`),
        axios.get(`${API}/instructors?school_id=${schoolRes.data.id}`),
        axios.get(`${API}/courses?school_id=${schoolRes.data.id}`),
        axios.get(`${API}/schools/${schoolRes.data.id}/bookings`, { withCredentials: true })
      ]);
      
      console.log('Admin school details fetched:', {
        locations: locsRes.data.length,
        instructors: instsRes.data.length,
        programs: progsRes.data.length,
        bookings: booksRes.data.length
      });
      
      setAdminLocations(locsRes.data);
      setAdminInstructors(instsRes.data);
      setAdminPrograms(progsRes.data);
      setAdminBookings(booksRes.data);
    } catch (error) {
      console.error('Failed to fetch admin school data:', error);
      console.error('Error details:', error.response?.data);
      // Only show toast for non-404 errors (404 can happen during auth initialization)
      if (error.response?.status !== 404) {
        toast.error(`Failed to load school data: ${error.response?.data?.detail || error.message}`);
      }
    }
  };

  const handleApproveSchool = async (schoolId) => {
    try {
      await axios.patch(`${API}/schools/${schoolId}/approve`, {}, { withCredentials: true });
      toast.success('School approved successfully');
      fetchAdminData();
    } catch (error) {
      console.error('Failed to approve school:', error);
      toast.error('Failed to approve school');
    }
  };

  const handleApproveFirstCourse = async (courseId) => {
    try {
      await axios.patch(`${API}/courses/${courseId}/approve-first`, {}, { withCredentials: true });
      toast.success('First course approved! School can now create courses without approval.');
      fetchAdminData();
    } catch (error) {
      console.error('Failed to approve course:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve course');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Branding Management
  const fetchBranding = async () => {
    try {
      const response = await axios.get(`${API}/branding`);
      setBrandingData(response.data);
    } catch (error) {
      console.error('Failed to fetch branding:', error);
      toast.error('Failed to load branding settings');
    }
  };

  const fetchAnalytics = async (days = 30) => {
    try {
      setAnalyticsLoading(true);
      const response = await axios.get(`${API}/analytics/overview?days=${days}`, {
        withCredentials: true
      });
      setAnalyticsData(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  const fetchRealtimeAnalytics = async () => {
    try {
      const response = await axios.get(`${API}/analytics/realtime`, {
        withCredentials: true
      });
      setRealtimeData(response.data);
    } catch (error) {
      console.debug('Realtime analytics unavailable');
    }
  };


  
  const fetchPayoutSummary = async () => {
    try {
      setPayoutLoading(true);
      const response = await axios.get(`${API}/admin/payout-requests/summary`, { withCredentials: true });
      setPayoutSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch payout summary:', error);
    } finally {
      setPayoutLoading(false);
    }
  };

  
  
  
  const fetchFunnel = async (days = 30) => {
    try {
      const res = await axios.get(`${API}/analytics/funnel?days=${days}`, { withCredentials: true });
      setFunnelData(res.data);
    } catch (err) { console.error('Funnel fetch error:', err); }
  };

  const fetchBehaviorReport = async (days = 7) => {
    try {
      setReportLoading(true);
      const res = await axios.get(`${API}/analytics/behavior-report?days=${days}`, { withCredentials: true });
      setBehaviorReport(res.data);
    } catch (err) { toast.error('Failed to generate behavior report'); }
    finally { setReportLoading(false); }
  };


  const fetchChatbotAnalytics = async (days = 30) => {
    setChatbotAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/ai/analytics?days=${days}`, { withCredentials: true });
      setChatbotAnalytics(res.data);
    } catch (err) { console.error('Failed to load chatbot analytics'); }
    setChatbotAnalyticsLoading(false);
  };

  const fetchStripeBalance = async () => {
    try {
      const res = await axios.get(`${API}/admin/stripe/balance`, { withCredentials: true });
      setStripeBalance(res.data);
    } catch (err) { console.error('Failed to load Stripe balance'); }
  };

  const handleProcessStripePayout = async (payoutId) => {
    if (!window.confirm('Process this payout via Stripe? This will attempt an automated transfer.')) return;
    setProcessingPayout(payoutId);
    try {
      const res = await axios.post(`${API}/admin/payout-requests/${payoutId}/process`, {}, { withCredentials: true });
      toast.success(`Payout processed! Method: ${res.data.method || 'stripe'}`);
      fetchPayoutSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process payout');
    }
    setProcessingPayout(null);
  };

  const fetchDriveStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/ai/drive/status`, { withCredentials: true });
      setDriveStatus(res.data);
    } catch (err) { console.error('Drive status error:', err); }
  };

  const triggerDriveSync = async () => {
    try {
      setDriveSyncing(true);
      const res = await axios.post(`${API}/admin/ai/drive/sync`, {}, { withCredentials: true });
      toast.success(`Drive sync complete: ${res.data.synced} files synced`);
      fetchDriveStatus();
      fetchKnowledge();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Drive sync failed');
    } finally { setDriveSyncing(false); }
  };

  const previewDriveFiles = async () => {
    try {
      const res = await axios.get(`${API}/admin/ai/drive/files`, { withCredentials: true });
      setDriveFiles(res.data.files);
    } catch (err) { toast.error('Failed to list Drive files'); }
  };

  const fetchKnowledge = async () => {
    try {
      setKnowledgeLoading(true);
      const res = await axios.get(`${API}/admin/ai/knowledge`, { withCredentials: true });
      setKnowledgeArticles(res.data.articles || []);
    } catch (err) { console.error('Knowledge fetch error:', err); }
    finally { setKnowledgeLoading(false); }
  };

  const saveKnowledgeArticle = async () => {
    if (!newArticle.title || !newArticle.content) {
      toast.error('Title and content are required');
      return;
    }
    try {
      if (editingArticle) {
        await axios.patch(`${API}/admin/ai/knowledge/${editingArticle.id}`, newArticle, { withCredentials: true });
        toast.success('Article updated');
      } else {
        await axios.post(`${API}/admin/ai/knowledge`, newArticle, { withCredentials: true });
        toast.success('Article added to AI knowledge base');
      }
      setNewArticle({ title: '', content: '', category: 'general', source_url: '' });
      setEditingArticle(null);
      setShowKnowledgeForm(false);
      fetchKnowledge();
    } catch (err) { toast.error('Failed to save article'); }
  };

  const toggleKnowledgeActive = async (articleId, currentActive) => {
    try {
      await axios.patch(`${API}/admin/ai/knowledge/${articleId}`, { active: !currentActive }, { withCredentials: true });
      fetchKnowledge();
    } catch (err) { toast.error('Failed to update'); }
  };

  const deleteKnowledgeArticle = async (articleId) => {
    if (!window.confirm('Delete this knowledge article?')) return;
    try {
      await axios.delete(`${API}/admin/ai/knowledge/${articleId}`, { withCredentials: true });
      toast.success('Article deleted');
      fetchKnowledge();
    } catch (err) { toast.error('Failed to delete'); }
  };

  const handleMarkPayout = async (payoutId, status) => {
    try {
      await axios.patch(`${API}/admin/payout-requests/${payoutId}`, {
        status,
        payout_method: payoutForm.payout_method || undefined,
        transaction_reference: payoutForm.transaction_reference || undefined,
        admin_notes: payoutForm.admin_notes || undefined,
      }, { withCredentials: true });
      toast.success(`Payout ${status === 'paid' ? 'marked as paid' : 'updated'}!`);
      setPayoutDialog(null);
      setPayoutForm({ payout_method: '', transaction_reference: '', admin_notes: '' });
      fetchPayoutSummary();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update payout');
    }
  };

  const handleBrandingUpdate = async () => {
    setUploadingBranding(true);
    try {
      await axios.put(`${API}/admin/branding`, brandingData, { withCredentials: true });
      toast.success('Branding updated successfully!');
      fetchBranding();
    } catch (error) {
      console.error('Failed to update branding:', error);
      toast.error('Failed to update branding');
    } finally {
      setUploadingBranding(false);
    }
  };

  const handleBrandingImageUpload = async (file, field) => {
    try {
      console.log("Uploading image for field:", field);
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await axios.post(`${API}/upload/image`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      console.log("Upload response:", response.data);
      const imageUrl = response.data.url.replace("/uploads/", "/api/uploads/");
      
      // Update state - user must click Save to persist
      setBrandingData(prev => ({ ...prev, [field]: imageUrl }));
      
      toast.success(`Image uploaded! Click Save to apply changes.`);
      return imageUrl;
    } catch (error) {
      console.error("Upload error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Unknown error";
      toast.error(`Upload failed: ${errorMsg}`);
      throw error;
    }
  };
  // Admin School - Location handlers
  const handleCreateLocation = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...programForm,
        martial_arts_style: programForm.martial_arts_style === "Other" ? programForm.custom_martial_art : programForm.martial_arts_style, 
        ...locationForm, 
        capacity: parseInt(locationForm.capacity), 
        facilities: locationForm.facilities 
      };
      await axios.post(`${API}/locations`, payload, { withCredentials: true });
      toast.success('Location created successfully');
      setLocationDialogOpen(false);
      setLocationForm({ 
        name: '', address: '', city: '', prefecture: '', capacity: '', facilities: [], 
        facility_images: [], google_maps_url: '', description: '', latitude: null, longitude: null 
      });
      fetchAdminSchoolData();
    } catch (error) {
      toast.error('Failed to create location');
    }
  };

  const handleDeleteLocation = async (id) => {
    console.log('handleDeleteLocation called with id:', id);
    if (!window.confirm('Are you sure you want to delete this location?')) {
      console.log('User cancelled location deletion');
      return;
    }
    console.log('Attempting to delete location...');
    try {
      await axios.delete(`${API}/locations/${id}`, { withCredentials: true });
      console.log('Location deleted successfully');
      toast.success('Location deleted successfully');
      fetchAdminSchoolData();
    } catch (error) {
      console.error('Failed to delete location:', error);
      toast.error('Failed to delete location');
    }
  };

  // Admin School - Instructor handlers
  const handleCreateInstructor = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...programForm,
        martial_arts_style: programForm.martial_arts_style === "Other" ? programForm.custom_martial_art : programForm.martial_arts_style, 
        ...instructorForm, 
        years_experience: instructorForm.years_experience ? parseInt(instructorForm.years_experience) : null, 
        specialties: instructorForm.specialties.split(',').map(s => s.trim()).filter(s => s) 
      };
      await axios.post(`${API}/instructors`, payload, { withCredentials: true });
      toast.success('Instructor added successfully');
      setInstructorDialogOpen(false);
      setInstructorForm({ name: '', email: '', phone: '', rank: '', years_experience: '', bio: '', specialties: '' });
      fetchAdminSchoolData();
    } catch (error) {
      toast.error('Failed to add instructor');
    }
  };

  const handleDeleteInstructor = async (id) => {
    console.log('handleDeleteInstructor called with id:', id);
    if (!window.confirm('Are you sure you want to delete this instructor?')) {
      console.log('User cancelled instructor deletion');
      return;
    }
    console.log('Attempting to delete instructor...');
    try {
      await axios.delete(`${API}/instructors/${id}`, { withCredentials: true });
      console.log('Instructor deleted successfully');
      toast.success('Instructor deleted successfully');
      fetchAdminSchoolData();
    } catch (error) {
      console.error('Failed to delete instructor:', error);
      toast.error('Failed to delete instructor');
    }
  };

  // Admin School - Program handlers
  const handleCreateProgram = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...programForm,
        martial_arts_style: programForm.martial_arts_style === "Other" ? programForm.custom_martial_art : programForm.martial_arts_style,
        price: parseFloat(programForm.price),
        capacity: parseInt(programForm.capacity)
      };
      
      if (editingProgram) {
        // Update existing program
        await axios.put(`${API}/courses/${editingProgram.id}`, payload, { withCredentials: true });
        toast.success('Program updated successfully');
      } else {
        // Create new program
        await axios.post(`${API}/courses`, payload, { withCredentials: true });
        toast.success('Program created successfully');
      }
      
      setProgramDialogOpen(false);
      setEditingProgram(null);
      setProgramForm({
        location_id: '', instructor_id: '', title: '', description: '', martial_arts_style: 'Aikido', custom_martial_art: '', is_koryu: false,
        course_category: 'Martial Arts', category: 'Martial Arts', experience_level: 'beginner', 
        class_type: 'group', price: '', currency: 'JPY', capacity: '', prerequisites: '', 
        start_date: '', end_date: '', daily_start_time: '09:00', daily_end_time: '17:00', image_url: ''
      });
      fetchAdminSchoolData();
    } catch (error) {
      toast.error(editingProgram ? 'Failed to update program' : 'Failed to create program');
    }
  };

  const handleEditProgram = (program) => {
    setEditingProgram(program);
    setProgramForm({
      location_id: program.location_id || '',
      instructor_id: program.instructor_id || '',
      title: program.title || '',
      description: program.description || '',
      martial_arts_style: program.martial_arts_style || 'Aikido',
      course_category: program.course_category || 'Martial Arts',
      category: program.category || 'Martial Arts',
      experience_level: program.experience_level || 'beginner',
      class_type: program.class_type || 'group',
      price: program.price?.toString() || '',
      currency: program.currency || 'JPY',
      capacity: program.capacity?.toString() || '',
      prerequisites: program.prerequisites || '',
      start_date: program.start_date || '',
      end_date: program.end_date || '',
      daily_start_time: program.daily_start_time || '09:00',
      daily_end_time: program.daily_end_time || '17:00',
      image_url: program.image_url || ''
    });
    setProgramDialogOpen(true);
  };

  const handleDeleteProgram = async (id) => {
    console.log('handleDeleteProgram called with id:', id);
    if (!window.confirm('Are you sure you want to delete this program?')) {
      console.log('User cancelled program deletion');
      return;
    }
    console.log('Attempting to delete program...');
    try {
      await axios.delete(`${API}/courses/${id}`, { withCredentials: true });
      console.log('Program deleted successfully');
      toast.success('Program deleted successfully');
      fetchAdminSchoolData();
      fetchAdminData(); // Also refresh all programs list
    } catch (error) {
      console.error('Failed to delete program:', error);
      toast.error('Failed to delete program');
    }
  };

  // ==================== ADMIN EDIT ALL PROGRAMS FUNCTIONS ====================
  
  const handleOpenEditProgramDialog = (program) => {
    setEditingProgram(program);
    setEditProgramForm({
      title: program.title || '',
      description: program.description || '',
      martial_arts_style: program.martial_arts_style || 'Aikido',
      category: program.category || 'Martial Arts',
      course_category: program.course_category || 'Martial Arts',
      experience_level: program.experience_level || 'beginner',
      class_type: program.class_type || 'group',
      price: program.price?.toString() || '',
      currency: program.currency || 'JPY',
      capacity: program.capacity?.toString() || '',
      prerequisites: program.prerequisites || '',
      start_date: program.start_date || '',
      end_date: program.end_date || '',
      daily_start_time: program.daily_start_time || '09:00',
      daily_end_time: program.daily_end_time || '17:00',
      image_url: program.image_url || '',
      location_id: program.location_id || '',
      instructor_id: program.instructor_id || ''
    });
    setEditProgramDialog(true);
  };

  const handleSaveEditedProgram = async () => {
    if (!editingProgram) return;
    
    try {
      const payload = {
        ...editProgramForm,
        price: parseFloat(editProgramForm.price) || 0,
        capacity: parseInt(editProgramForm.capacity) || 10
      };
      
      await axios.put(`${API}/courses/${editingProgram.id}`, payload, { withCredentials: true });
      toast.success('Program updated successfully');
      setEditProgramDialog(false);
      setEditingProgram(null);
      fetchAdminData(); // Refresh all programs
    } catch (error) {
      console.error('Failed to update program:', error);
      toast.error(error.response?.data?.detail || 'Failed to update program');
    }
  };

  // ==================== FACEBOOK POSTING FUNCTIONS ====================

  const handlePostToFacebook = async (program = null) => {
    setFacebookPosting(true);
    try {
      let response;
      if (program) {
        // Post specific program
        response = await axios.post(`${API}/admin/facebook/post-course/${program.id}`, {}, { withCredentials: true });
        if (response.data.success) {
          toast.success(`Posted "${program.title}" to Facebook!`);
        } else {
          toast.error(response.data.error || 'Facebook post failed');
        }
      } else {
        // Post random program
        response = await axios.post(`${API}/admin/facebook/trigger-scheduled-post`, {}, { withCredentials: true });
        if (response.data.success) {
          toast.success('Random program posted to Facebook!');
        } else {
          toast.error(response.data.error || 'Facebook post failed');
        }
      }
      // Refresh posts log
      fetchFacebookPostsLog();
    } catch (error) {
      console.error('Facebook post error:', error);
      toast.error(error.response?.data?.detail || 'Failed to post to Facebook');
    } finally {
      setFacebookPosting(false);
    }
  };

  const fetchFacebookPostsLog = async () => {
    try {
      const response = await axios.get(`${API}/admin/facebook/scheduled-posts-log?limit=20`, { withCredentials: true });
      setFacebookPostsLog(response.data.posts || []);
    } catch (error) {
      console.error('Failed to fetch Facebook posts log:', error);
    }
  };

  const handleResetDailyPosts = async () => {
    try {
      await axios.delete(`${API}/admin/facebook/reset-daily-posts`, { withCredentials: true });
      toast.success('Daily posts tracker reset - courses can be posted again today');
    } catch (error) {
      toast.error('Failed to reset daily posts');
    }
  };

  // ==================== NOTIFICATIONS FUNCTIONS ====================

  const fetchNotifications = useCallback(async (isPolling = false) => {
    try {
      const response = await axios.get(`${API}/admin/notifications`, { withCredentials: true });
      const newNotifications = response.data.notifications || [];
      const newUnreadCount = response.data.unread_count || 0;
      setNotifications(newNotifications);
      setUnreadCount(newUnreadCount);
      // If polling detected NEW unread notifications, show a toast alert
      if (isPolling && newUnreadCount > prevUnreadCountRef.current) {
        const newPending = newNotifications.filter(n => !n.read && n.type === 'pending_course_approval');
        if (newPending.length > 0) {
          const latest = newPending[0];
          toast.warning(`New course pending approval: ${latest.message}`, {
            duration: 10000,
            action: {
              label: 'Review',
              onClick: () => setActiveTab('pending-courses'),
            },
          });
        }
      }
      prevUnreadCountRef.current = newUnreadCount;
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  }, []);

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchNotifications(true);
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

    const handleMarkNotificationRead = async (notificationId) => {
    try {
      await axios.post(`${API}/admin/notifications/${notificationId}/read`, {}, { withCredentials: true });
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark notification read:', error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/admin/notifications/mark-all-read`, {}, { withCredentials: true });
      fetchNotifications();
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark notifications read');
    }
  };

  // ==================== USER MANAGEMENT FUNCTIONS ====================

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      // Validate instructor requires school
      if (userForm.role === 'instructor' && !userForm.school_id) {
        toast.error('Please select a school for instructor');
        return;
      }
      
      if (editingUser) {
        // Update existing user
        const updateData = {
          name: userForm.name,
          email: userForm.email,
          role: userForm.role,
          phone: userForm.phone || null,
          school_id: userForm.school_id || null,
          instructor_id: userForm.instructor_id || null
        };
        
        // Include password only if it's been changed (not empty)
        if (userForm.password && userForm.password.trim() !== '') {
          if (userForm.password.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
          }
          updateData.password = userForm.password;
        }
        
        await axios.put(`${API}/admin/users/${editingUser.id}`, updateData, { withCredentials: true });
        toast.success('User updated successfully');
      } else {
        // Create new user
        if (!userForm.password || userForm.password.length < 6) {
          toast.error('Password must be at least 6 characters');
          return;
        }
        await axios.post(`${API}/admin/users`, userForm, { withCredentials: true });
        toast.success('User created successfully');
      }
      
      setUserDialogOpen(false);
      setEditingUser(null);
      setUserForm({ name: '', email: '', password: '', role: 'student', phone: '', school_id: '', instructor_id: '' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || (editingUser ? 'Failed to update user' : 'Failed to create user'));
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      name: user.name,
      email: user.email,
      password: '', // Don't populate password
      role: user.role,
      phone: user.phone || '',
      school_id: user.school_id || '',
      instructor_id: user.instructor_id || ''
    });
    setUserDialogOpen(true);
  };

  const handleDeleteUser = async (userId, userName) => {
    console.log('handleDeleteUser called with:', userId, userName);
    
    if (!window.confirm(`Are you sure you want to delete user "${userName}"? This will delete all their related data (schools, locations, instructors, courses, bookings, sessions).`)) {
      console.log('User cancelled deletion');
      return;
    }
    
    console.log('Attempting to delete user...');
    
    try {
      const response = await axios.delete(`${API}/admin/users/${userId}`, { 
        withCredentials: true 
      });
      
      console.log('Delete response:', response.data);
      toast.success(`User "${userName}" and all related data deleted successfully`);
      
      // Show what was deleted
      if (response.data.deleted) {
        console.log('Deleted items:', response.data.deleted);
      }
      
      fetchUsers();
      fetchAdminSchoolData(); // Refresh school data too
    } catch (error) {
      console.error('Failed to delete user:', error);
      console.error('Error details:', error.response?.data);
      const errorMsg = error.response?.data?.detail || 'Failed to delete user';
      toast.error(errorMsg);
    }
  };

  const handleDeleteSchool = async (schoolId, schoolName) => {
    console.log('handleDeleteSchool called with:', schoolId, schoolName);
    if (!window.confirm(`Are you sure you want to delete school "${schoolName}"? This will delete all locations, instructors, courses, and bookings associated with this school.`)) {
      console.log('User cancelled school deletion');
      return;
    }
    
    console.log('Attempting to delete school...');
    try {
      const response = await axios.delete(`${API}/admin/schools/${schoolId}`, { 
        withCredentials: true 
      });
      
      console.log('School deleted successfully:', response.data);
      toast.success(`School "${schoolName}" and all related data deleted successfully`);
      
      if (response.data.deleted) {
        console.log('Deleted items:', response.data.deleted);
      }
      
      fetchAdminData();
      fetchAdminSchoolData();
    } catch (error) {
      console.error('Failed to delete school:', error);
      console.error('Error details:', error.response?.data);
      const errorMsg = error.response?.data?.detail || 'Failed to delete school';
      toast.error(errorMsg);
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to delete this booking?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`, { 
        withCredentials: true 
      });
      
      toast.success('Booking deleted successfully');
      fetchAdminSchoolData();
    } catch (error) {
      console.error('Failed to delete booking:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to delete booking';
      toast.error(errorMsg);
    }
  };

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
    user.role.toLowerCase().includes(userSearchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen overflow-x-hidden flex items-center justify-center bg-gradient-to-br from-slate-50 to-emerald-50">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-700"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-slate-50 to-emerald-50">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-lg border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3 cursor-pointer" onClick={() => navigate('/')}>
            {brandingData.site_logo_url && (
              <div className="relative -my-3" style={{ filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))' }}>
                <div className="w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full bg-white border-[3px] border-emerald-600 overflow-hidden flex items-center justify-center">
                  <img src={brandingData.site_logo_url} alt="Logo" className="w-full h-full object-cover" />
                </div>
              </div>
            )}
            <h1 data-testid="site-logo" className="text-base sm:text-lg md:text-xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>{brandingData.site_name || 'Train In Japan'}</h1>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <span className="text-slate-700 font-medium">Admin Panel</span>
            <Button data-testid="logout-btn" onClick={handleLogout} variant="outline">Logout</Button>
          </div>
        </div>
      </nav>

      {/* Dashboard Content */}
      <div data-testid="admin-dashboard" className="max-w-7xl mx-auto px-4 md:px-6 py-12">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>Admin Dashboard</h1>
          
          {/* Notification Bell */}
          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              data-testid="notification-bell-btn"
              className={`relative p-2 rounded-full hover:bg-slate-100 transition-colors ${unreadCount > 0 ? 'animate-bounce' : ''}`}
              title={unreadCount > 0 ? `${unreadCount} course(s) pending approval` : 'Notifications'}
            >
              <span className="text-2xl">🔔</span>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </button>
            
            {/* Notifications Dropdown */}
            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-slate-200 z-50 max-h-96 overflow-y-auto">
                <div className="p-3 border-b border-slate-200 flex justify-between items-center">
                  <span className="font-semibold text-slate-900">Notifications</span>
                  {unreadCount > 0 && (
                    <button 
                      onClick={handleMarkAllRead}
                      className="text-xs text-emerald-600 hover:text-emerald-700"
                    >
                      Mark all read
                    </button>
                  )}
                </div>
                {notifications.length === 0 ? (
                  <div className="p-4 text-center text-slate-500 text-sm">No notifications</div>
                ) : (
                  notifications.map((notif) => (
                    <div 
                      key={notif.id}
                      className={`p-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer ${!notif.read ? 'bg-emerald-50' : ''}`}
                      onClick={() => {
                        handleMarkNotificationRead(notif.id);
                        if (notif.type === 'pending_course_approval') {
                          setActiveTab('pending-courses');
                          setShowNotifications(false);
                        }
                      }}
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-lg">{notif.type === 'pending_course_approval' ? '📋' : '🔔'}</span>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-slate-900">{notif.title}</p>
                          <p className="text-xs text-slate-600 mt-0.5">{notif.message}</p>
                          <p className="text-xs text-slate-400 mt-1">{new Date(notif.created_at).toLocaleString()}</p>
                        </div>
                        {!notif.read && <span className="w-2 h-2 bg-emerald-500 rounded-full mt-1"></span>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        {/* Pending Course Alert Banner */}
        {unreadCount > 0 && (
          <div
            data-testid="pending-approval-banner"
            className="mb-6 flex items-center justify-between bg-amber-50 border-l-4 border-amber-500 rounded-lg px-5 py-4 shadow-sm cursor-pointer hover:bg-amber-100 transition-colors"
            onClick={() => { setActiveTab('pending-courses'); setShowNotifications(false); }}
          >
            <div className="flex items-center gap-3">
              <span className="text-2xl">📋</span>
              <div>
                <p className="font-semibold text-amber-900 text-sm">
                  {unreadCount} course{unreadCount !== 1 ? 's' : ''} pending first approval
                </p>
                <p className="text-xs text-amber-700 mt-0.5">Click to review and approve</p>
              </div>
            </div>
            <span className="text-amber-600 text-sm font-medium hover:underline">Review &rarr;</span>
          </div>
        )}

        {/* Stats Cards - Clickable */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow hover:border-emerald-500"
            onClick={() => setActiveTab("schools")}
          >
            <CardHeader>
              <CardTitle className="text-sm font-medium text-slate-600">Total Schools</CardTitle>
            </CardHeader>
            <CardContent>
              <p data-testid="total-schools" className="text-3xl font-bold text-slate-900">{stats?.total_schools || 0}</p>
              <p className="text-xs text-slate-500 mt-1">{stats?.approved_schools || 0} approved</p>
              <p className="text-xs text-emerald-600 mt-2 font-medium">→ Click to view</p>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow hover:border-emerald-500"
            onClick={() => setActiveTab("pending-courses")}
          >
            <CardHeader>
              <CardTitle className="text-sm font-medium text-slate-600">Pending Courses</CardTitle>
            </CardHeader>
            <CardContent>
              <p data-testid="pending-classes" className="text-3xl font-bold text-emerald-700">{stats?.pending_courses || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Awaiting approval</p>
              <p className="text-xs text-emerald-600 mt-2 font-medium">→ Click to approve</p>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow hover:border-emerald-500"
            onClick={() => setActiveTab("programs")}
          >
            <CardHeader>
              <CardTitle className="text-sm font-medium text-slate-600">Total Programs</CardTitle>
            </CardHeader>
            <CardContent>
              <p data-testid="total-programs" className="text-3xl font-bold text-slate-900">{stats?.total_courses || 0}</p>
              <p className="text-xs text-slate-500 mt-1">{stats?.active_courses || 0} active</p>
              <p className="text-xs text-emerald-600 mt-2 font-medium">→ Click to view</p>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow hover:border-emerald-500"
            onClick={() => setActiveTab("programs")}
          >
            <CardHeader>
              <CardTitle className="text-sm font-medium text-slate-600">Total Bookings</CardTitle>
            </CardHeader>
            <CardContent>
              <p data-testid="total-bookings" className="text-3xl font-bold text-slate-900">{stats?.total_bookings || 0}</p>
              <p className="text-xs text-slate-500 mt-1">{stats?.paid_bookings || 0} paid</p>
              <p className="text-xs text-emerald-600 mt-2 font-medium">→ Click to view</p>
            </CardContent>
          </Card>
        </div>

        {/* Locations & Instructors - Clickable */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow hover:border-emerald-500"
            onClick={() => setActiveTab("schools")}
          >
            <CardHeader>
              <CardTitle className="text-sm font-medium text-slate-600">Total Locations</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-slate-900">{stats?.total_locations || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Training facilities</p>
              <p className="text-xs text-emerald-600 mt-2 font-medium">→ Click to view schools</p>
            </CardContent>
          </Card>
          <Card 
            className="cursor-pointer hover:shadow-lg transition-shadow hover:border-emerald-500"
            onClick={() => setActiveTab("schools")}
          >
            <CardHeader>
              <CardTitle className="text-sm font-medium text-slate-600">Total Instructors</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-slate-900">{stats?.total_instructors || 0}</p>
              <p className="text-xs text-slate-500 mt-1">Registered instructors</p>
              <p className="text-xs text-emerald-600 mt-2 font-medium">→ Click to view schools</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs - Mobile Responsive */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-3 md:grid-cols-9 h-auto gap-1">
            <TabsTrigger data-testid="pending-courses-tab" value="pending-courses" className="text-xs md:text-sm py-2 px-2">
              <span className="hidden md:inline">Pending First Courses</span>
              <span className="md:hidden">Pending</span>
            </TabsTrigger>
            <TabsTrigger data-testid="schools-tab" value="schools" className="text-xs md:text-sm py-2 px-2">
              Schools
            </TabsTrigger>
            <TabsTrigger data-testid="programs-tab" value="programs" className="text-xs md:text-sm py-2 px-2">
              <span className="hidden md:inline">All Programs</span>
              <span className="md:hidden">Programs</span>
            </TabsTrigger>
            <TabsTrigger data-testid="users-tab" value="users" className="text-xs md:text-sm py-2 px-2">
              Users
            </TabsTrigger>
            <TabsTrigger data-testid="branding-tab" value="branding" className="text-xs md:text-sm py-2 px-2">
              🎨 Branding
            </TabsTrigger>
                                    <TabsTrigger data-testid="knowledge-tab" value="knowledge" className="flex items-center gap-2 data-[state=active]:bg-emerald-50"><span className="hidden sm:inline">AI Knowledge</span><span className="sm:hidden">AI</span></TabsTrigger>
                                    <TabsTrigger data-testid="financial-tab" value="financial" className="text-xs md:text-sm py-2 px-2">
              💰 Financial
            </TabsTrigger>
            <TabsTrigger data-testid="chatbot-tab" value="chatbot" className="text-xs md:text-sm py-2 px-2">
              AI Chat
            </TabsTrigger>
            <TabsTrigger data-testid="analytics-tab" value="analytics" className="text-xs md:text-sm py-2 px-2">
              📊 Analytics
            </TabsTrigger>
            <TabsTrigger data-testid="messages-tab" value="messages" className="text-xs md:text-sm py-2 px-2">
              Messages
            </TabsTrigger>
            <TabsTrigger data-testid="my-school-tab" value="my-school" className="text-xs md:text-sm py-2 px-2">
              My School
            </TabsTrigger>
          </TabsList>

          {/* Pending First Courses Tab */}
          <TabsContent value="pending-courses" className="mt-6">
            <h2 className="text-base sm:text-lg md:text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Spectral, serif' }}>Approve First Courses</h2>
            <p className="text-slate-600 mb-6">Each school needs approval for their first course. After approval, they can create courses freely.</p>

            {programs.filter(p => p.status === 'pending_first_approval').length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-slate-600">No courses pending approval.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {programs.filter(p => p.status === 'pending_first_approval').map((program, idx) => (
                  <Card key={program.id} className="overflow-hidden">
                    <div className="flex">
                      <img src={program.image_url} alt={program.title} className="w-48 h-48 object-cover" />
                      <div className="flex-1 p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h3 className="text-xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Spectral, serif' }}>{program.title}</h3>
                            <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-3 py-1 rounded-full">{program.category}</span>
                          </div>
                          <span className="text-xs font-semibold px-3 py-1 rounded-full bg-yellow-100 text-yellow-700">
                            First Course - Needs Approval
                          </span>
                        </div>
                        <p className="text-slate-600 mb-4">{program.description}</p>
                        <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                          <p className="text-slate-600"><strong>Price:</strong> ¥{program.price?.toLocaleString('ja-JP')}</p>
                          <p className="text-slate-600"><strong>Duration:</strong> {program.duration}</p>
                          <p className="text-slate-600"><strong>Capacity:</strong> {program.capacity} students</p>
                          <p className="text-slate-600"><strong>Level:</strong> {program.experience_level}</p>
                        </div>
                        <Button
                          onClick={() => handleApproveFirstCourse(program.id)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          ✅ Approve First Course
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Schools Tab */}
          <TabsContent value="schools" className="mt-6">
            <h2 className="text-base sm:text-lg md:text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Spectral, serif' }}>Manage Schools</h2>

            {schools.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p data-testid="no-schools-msg" className="text-slate-600">No schools registered yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {schools.map((school, idx) => (
                  <Card key={school.id} data-testid={`school-card-${idx}`}>
                    <CardHeader>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-xl" style={{ fontFamily: 'Spectral, serif' }}>{school.name}</CardTitle>
                          <p className="text-slate-600 mt-1">{school.location}</p>
                        </div>
                        <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                          school.approved ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>{school.approved ? 'Approved' : 'Pending'}</span>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-slate-600 mb-4">{school.description}</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-slate-600"><strong>Email:</strong> {school.contact_email}</p>
                          {school.contact_phone && <p className="text-slate-600"><strong>Phone:</strong> {school.contact_phone}</p>}
                        </div>
                        <div>
                          {school.website && <p className="text-slate-600"><strong>Website:</strong> <a href={school.website} target="_blank" rel="noopener noreferrer" className="text-emerald-700 hover:underline">{school.website}</a></p>}
                        </div>
                      </div>
                      <div className="mt-4 flex gap-2">
                        {!school.approved && (
                          <Button 
                            data-testid={`approve-school-${idx}`}
                            onClick={() => handleApproveSchool(school.id)} 
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Approve School
                          </Button>
                        )}
                        <Button
                          data-testid={`edit-school-${idx}`}
                          onClick={() => {
                            setEditingSchool(school);
                            setEditSchoolDialog(true);
                          }}
                          variant="outline"
                          className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                        >
                          ✏️ Edit School
                        </Button>
                        <button 
                          type="button"
                          data-testid={`delete-school-${idx}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteSchool(school.id, school.name);
                          }}
                          className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 active:bg-red-800 transition-colors cursor-pointer"
                        >
                          Delete School
                        </button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Programs Tab */}
          <TabsContent value="programs" className="mt-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-base sm:text-lg md:text-xl font-bold text-slate-900" style={{ fontFamily: 'Spectral, serif' }}>All Programs</h2>
              <Button
                onClick={() => handlePostToFacebook()}
                disabled={facebookPosting || programs.length === 0}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {facebookPosting ? '⏳ Posting...' : '📘 Post Random to Facebook'}
              </Button>
            </div>

            {programs.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p data-testid="no-programs-msg" className="text-slate-600">No programs created yet.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {programs.map((program, idx) => (
                  <Card key={program.id} data-testid={`program-card-${idx}`} className="overflow-hidden">
                    {program.image_url && (
                      <img src={program.image_url} alt={program.title} className="w-full h-40 object-cover" />
                    )}
                    <CardHeader>
                      <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-3 py-1 rounded-full w-fit mb-2">{program.category}</span>
                      <CardTitle className="text-lg" style={{ fontFamily: 'Spectral, serif' }}>{program.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-600 mb-2"><strong>Price:</strong> ¥{program.price?.toLocaleString('ja-JP')}</p>
                      <p className="text-sm text-slate-600 mb-2"><strong>Duration:</strong> {program.duration}</p>
                      <p className="text-sm text-slate-500 mb-4"><strong>Status:</strong> {program.status}</p>
                      <div className="flex flex-col gap-2">
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                            onClick={() => handleOpenEditProgramDialog(program)}
                          >
                            ✏️ Edit
                          </Button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteProgram(program.id);
                            }}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 active:bg-red-800 transition-colors cursor-pointer"
                          >
                            Delete
                          </button>
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="w-full border-blue-500 text-blue-600 hover:bg-blue-50"
                          onClick={() => handlePostToFacebook(program)}
                          disabled={facebookPosting}
                        >
                          📘 Post to Facebook
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* My School Tab - Admin School Management */}
          <TabsContent value="my-school" className="mt-6">
            <h2 className="text-base sm:text-lg md:text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'Spectral, serif' }}>
              My School Dashboard
            </h2>
            <p className="text-slate-600 mb-6">
              Manage your admin school - create locations, add instructors, and publish programs.
            </p>

            {adminSchool && (
              <Card className="mb-6 bg-gradient-to-r from-emerald-50 to-teal-50 border-emerald-200">
                <CardHeader>
                  <CardTitle style={{ fontFamily: 'Spectral, serif' }}>{adminSchool.name}</CardTitle>
                  <CardDescription>{adminSchool.tagline}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-700 mb-2">{adminSchool.bio}</p>
                  <p className="text-sm text-slate-600">
                    <strong>Contact:</strong> {adminSchool.contact_email} | <strong>Location:</strong> {adminSchool.location}
                  </p>
                </CardContent>
                <CardFooter>
                  <Button
                    onClick={() => {
                      setEditingSchool(adminSchool);
                      setEditSchoolDialog(true);
                    }}
                    className="bg-emerald-700 hover:bg-emerald-800"
                  >
                    ✏️ Edit School Details & Branding
                  </Button>
                </CardFooter>
              </Card>
            )}

            {/* Admin School Stats - Clickable */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card 
                className="cursor-pointer hover:shadow-lg transition-shadow hover:border-emerald-500"
                onClick={() => setActiveSchoolTab("locations")}
              >
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-slate-600">Locations</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-slate-900">{adminLocations.length}</p>
                  <p className="text-xs text-emerald-600 mt-2 font-medium">→ Click to manage</p>
                </CardContent>
              </Card>
              <Card 
                className="cursor-pointer hover:shadow-lg transition-shadow hover:border-emerald-500"
                onClick={() => setActiveSchoolTab("instructors")}
              >
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-slate-600">Instructors</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-slate-900">{adminInstructors.length}</p>
                  <p className="text-xs text-emerald-600 mt-2 font-medium">→ Click to manage</p>
                </CardContent>
              </Card>
              <Card 
                className="cursor-pointer hover:shadow-lg transition-shadow hover:border-emerald-500"
                onClick={() => setActiveSchoolTab("admin-programs")}
              >
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-slate-600">Programs</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-slate-900">{adminPrograms.length}</p>
                  <p className="text-xs text-emerald-600 mt-2 font-medium">→ Click to manage</p>
                </CardContent>
              </Card>
              <Card 
                className="cursor-pointer hover:shadow-lg transition-shadow hover:border-emerald-500"
                onClick={() => setActiveSchoolTab("bookings")}
              >
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-slate-600">Bookings</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-slate-900">{adminBookings.length}</p>
                  <p className="text-xs text-emerald-600 mt-2 font-medium">→ Click to view</p>
                </CardContent>
              </Card>
            </div>

            {/* Admin School Sub-tabs - Mobile Responsive */}
            <Tabs value={activeSchoolTab} onValueChange={setActiveSchoolTab} className="w-full">
              <TabsList className="grid grid-cols-2 md:grid-cols-4 w-full h-auto">
                <TabsTrigger value="locations" className="text-xs md:text-sm py-2">Locations</TabsTrigger>
                <TabsTrigger value="instructors" className="text-xs md:text-sm py-2">Instructors</TabsTrigger>
                <TabsTrigger value="admin-programs" className="text-xs md:text-sm py-2">Programs</TabsTrigger>
                <TabsTrigger value="bookings" className="text-xs md:text-sm py-2">Bookings</TabsTrigger>
              </TabsList>

              {/* Locations Sub-tab */}
              <TabsContent value="locations" className="mt-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-900">Training Locations</h3>
                  <Dialog open={locationDialogOpen} onOpenChange={setLocationDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-emerald-700 hover:bg-emerald-800">+ Add Location</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Add Training Location</DialogTitle>
                        <DialogDescription>Create a new training facility location</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateLocation} className="space-y-4">
                        <div>
                          <Label>Location Name *</Label>
                          <Input 
                            value={locationForm.name} 
                            onChange={(e) => setLocationForm({...locationForm, name: e.target.value})}
                            required 
                          />
                        </div>
                        <div>
                          <Label>Pin Location on Map</Label>
                          <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={["places", "geocoding"]} onLoad={() => console.log("Maps loaded")}>
                            <LocationMapPicker
                              onLocationSelect={(locationData) => {
                                console.log('Location selected:', locationData);
                                setLocationForm({
                                  ...locationForm,
                                  latitude: locationData.lat,
                                  longitude: locationData.lng,
                                  address: locationData.address,
                                  prefecture: locationData.prefecture,
                                  city: locationData.city
                                });
                              }}
                              initialPosition={
                                locationForm.latitude && locationForm.longitude
                                  ? { lat: locationForm.latitude, lng: locationForm.longitude }
                                  : null
                              }
                              addressToGeocode={locationForm.address}
                            />
                          </APIProvider>
                        </div>
                        <div>
                          <Label>Address * {locationForm.address && <span className="text-xs text-emerald-600 ml-2">✓ Set from map</span>}</Label>
                          <Input 
                            value={locationForm.address} 
                            onChange={(e) => setLocationForm({...locationForm, address: e.target.value})}
                            required 
                            placeholder="Type address to search or click map above"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>City *</Label>
                            <Input 
                              value={locationForm.city} 
                              onChange={(e) => setLocationForm({...locationForm, city: e.target.value})}
                              required 
                            />
                          </div>
                          <div>
                            <Label>Prefecture *</Label>
                            <Input 
                              value={locationForm.prefecture} 
                              onChange={(e) => setLocationForm({...locationForm, prefecture: e.target.value})}
                              required 
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Capacity *</Label>
                          <Input 
                            type="number" 
                            value={locationForm.capacity} 
                            onChange={(e) => setLocationForm({...locationForm, capacity: e.target.value})}
                            required 
                          />
                        </div>
                        <div>
                          <Label>Facilities *</Label>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2 p-4 border rounded-lg bg-slate-50 max-h-[200px] overflow-y-auto">
                            {[
                              'Dojo', 'Training Hall', 'Tatami Mats', 'Weapon Storage',
                              'Changing Rooms', 'Showers', 'Lockers', 'Waiting Area',
                              'Viewing Gallery', 'Equipment Room', 'First Aid Room',
                              'Kitchen', 'Meditation Room', 'Outdoor Training Area',
                              'Parking', 'Wi-Fi', 'Air Conditioning', 'Heating'
                            ].map((facility) => (
                              <label key={facility} className="flex items-center space-x-2 cursor-pointer hover:bg-slate-100 p-2 rounded">
                                <input
                                  type="checkbox"
                                  checked={locationForm.facilities.includes(facility)}
                                  onChange={(e) => {
                                    const newFacilities = e.target.checked
                                      ? [...locationForm.facilities, facility]
                                      : locationForm.facilities.filter(f => f !== facility);
                                    setLocationForm({...locationForm, facilities: newFacilities});
                                  }}
                                  className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                                />
                                <span className="text-sm text-slate-700">{facility}</span>
                              </label>
                            ))}
                          </div>
                          <p className="text-xs text-slate-500 mt-2">
                            Selected: {locationForm.facilities.length > 0 ? locationForm.facilities.join(', ') : 'None'}
                          </p>
                        </div>
                        <div>
                          <Label>Description</Label>
                          <Textarea 
                            value={locationForm.description} 
                            onChange={(e) => setLocationForm({...locationForm, description: e.target.value})}
                            rows={3}
                          />
                        </div>
                        <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800">
                          Create Location
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>

                {adminLocations.length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <p className="text-slate-600">No locations yet. Add your first training location!</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {adminLocations.map((location) => (
                      <Card key={location.id}>
                        <CardHeader>
                          <CardTitle>{location.name}</CardTitle>
                          <CardDescription>{location.city}, {location.prefecture}</CardDescription>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-slate-600 mb-2">{location.address}</p>
                          <p className="text-sm text-slate-600"><strong>Capacity:</strong> {location.capacity} students</p>
                          {location.facilities && location.facilities.length > 0 && (
                            <p className="text-sm text-slate-600"><strong>Facilities:</strong> {location.facilities.join(', ')}</p>
                          )}
                          <button 
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteLocation(location.id);
                            }}
                            className="mt-4 px-3 py-1.5 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 active:bg-red-800 transition-colors cursor-pointer"
                          >
                            Delete
                          </button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Instructors Sub-tab */}
              <TabsContent value="instructors" className="mt-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-900">Instructors</h3>
                  <Dialog open={instructorDialogOpen} onOpenChange={setInstructorDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-emerald-700 hover:bg-emerald-800">+ Add Instructor</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Add Instructor</DialogTitle>
                        <DialogDescription>Add a new instructor to your school</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateInstructor} className="space-y-4">
                        <div>
                          <Label>Name *</Label>
                          <Input 
                            value={instructorForm.name} 
                            onChange={(e) => setInstructorForm({...instructorForm, name: e.target.value})}
                            required 
                          />
                        </div>
                        <div>
                          <Label>Email *</Label>
                          <Input 
                            type="email"
                            value={instructorForm.email} 
                            onChange={(e) => setInstructorForm({...instructorForm, email: e.target.value})}
                            required 
                          />
                        </div>
                        <div>
                          <Label>Phone</Label>
                          <Input 
                            value={instructorForm.phone} 
                            onChange={(e) => setInstructorForm({...instructorForm, phone: e.target.value})}
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Rank</Label>
                            <Input 
                              value={instructorForm.rank} 
                              onChange={(e) => setInstructorForm({...instructorForm, rank: e.target.value})}
                              placeholder="e.g., 5th Dan Black Belt"
                            />
                          </div>
                          <div>
                            <Label>Years of Experience</Label>
                            <Input 
                              type="number"
                              value={instructorForm.years_experience} 
                              onChange={(e) => setInstructorForm({...instructorForm, years_experience: e.target.value})}
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Bio</Label>
                          <Textarea 
                            value={instructorForm.bio} 
                            onChange={(e) => setInstructorForm({...instructorForm, bio: e.target.value})}
                            rows={3}
                          />
                        </div>
                        <div>
                          <Label>Specialties (comma-separated)</Label>
                          <Input 
                            value={instructorForm.specialties} 
                            onChange={(e) => setInstructorForm({...instructorForm, specialties: e.target.value})}
                            placeholder="Aikido, Judo, Weapons Training"
                          />
                        </div>
                        <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800">
                          Add Instructor
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>

                {adminInstructors.length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <p className="text-slate-600">No instructors yet. Add your first instructor!</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {adminInstructors.map((instructor) => (
                      <Card key={instructor.id}>
                        <CardHeader>
                          <CardTitle>{instructor.name}</CardTitle>
                          {instructor.rank && <CardDescription>{instructor.rank}</CardDescription>}
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-slate-600 mb-2">{instructor.email}</p>
                          {instructor.years_experience && (
                            <p className="text-sm text-slate-600 mb-2">
                              <strong>Experience:</strong> {instructor.years_experience} years
                            </p>
                          )}
                          {instructor.specialties && instructor.specialties.length > 0 && (
                            <p className="text-sm text-slate-600 mb-2">
                              <strong>Specialties:</strong> {instructor.specialties.join(', ')}
                            </p>
                          )}
                          <button 
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDeleteInstructor(instructor.id);
                            }}
                            className="mt-4 px-3 py-1.5 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 active:bg-red-800 transition-colors cursor-pointer"
                          >
                            Delete
                          </button>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Programs Sub-tab */}
              <TabsContent value="admin-programs" className="mt-6">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-xl font-bold text-slate-900">Training Programs</h3>
                  <Dialog open={programDialogOpen} onOpenChange={(open) => {
                    setProgramDialogOpen(open);
                    if (!open) {
                      setEditingProgram(null);
                      setProgramForm({
                        location_id: '', instructor_id: '', title: '', description: '', martial_arts_style: 'Aikido', custom_martial_art: '', is_koryu: false,
                        course_category: 'Martial Arts', category: 'Martial Arts', experience_level: 'beginner', 
                        class_type: 'group', price: '', currency: 'JPY', capacity: '', prerequisites: '', 
                        start_date: '', end_date: '', daily_start_time: '09:00', daily_end_time: '17:00', image_url: ''
                      });
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button className="bg-emerald-700 hover:bg-emerald-800">+ Create Program</Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>{editingProgram ? 'Edit Training Program' : 'Create Training Program'}</DialogTitle>
                        <DialogDescription>
                          {editingProgram ? 'Update your training program details' : 'Add a new training program to your school'}
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateProgram} className="space-y-4">
                        <div>
                          <Label>Program Title *</Label>
                          <Input 
                            value={programForm.title} 
                            onChange={(e) => setProgramForm({...programForm, title: e.target.value})}
                            required 
                          />
                        </div>
                        <div>
                          <Label>Description *</Label>
                          <Textarea 
                            value={programForm.description} 
                            onChange={(e) => setProgramForm({...programForm, description: e.target.value})}
                            rows={4}
                            required 
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Location *</Label>
                            <Select 
                              value={programForm.location_id} 
                              onValueChange={(value) => setProgramForm({...programForm, location_id: value})}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select location" />
                              </SelectTrigger>
                              <SelectContent>
                                {adminLocations.map((loc) => (
                                  <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label>Instructor (Optional)</Label>
                            <Select 
                              value={programForm.instructor_id} 
                              onValueChange={(value) => setProgramForm({...programForm, instructor_id: value})}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Select instructor" />
                              </SelectTrigger>
                              <SelectContent>
                                {adminInstructors.map((inst) => (
                                  <SelectItem key={inst.id} value={inst.id}>{inst.name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 mb-4"><div><Label>Course Category *</Label><Select value={programForm.course_category} onValueChange={(value) => setProgramForm({...programForm, course_category: value})}><SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger><SelectContent><SelectItem value="Martial Arts">Martial Arts</SelectItem><SelectItem value="Cultural Arts">Cultural Arts</SelectItem></SelectContent></Select></div></div>
                        <div className="grid grid-cols-3 gap-4">
                                                                              <div className="col-span-3">
                            <Label>Martial Arts Style *</Label>
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-2 max-h-[300px] overflow-y-auto border rounded-lg p-4 bg-slate-50">
                              {[
                                { value: 'Aikido', label: 'Aikido (合気道)' },
                                { value: 'Judo', label: 'Judo (柔道)' },
                                { value: 'Karate', label: 'Karate (空手)' },
                                { value: 'Kendo', label: 'Kendo (剣道)' },
                                { value: 'Iaido', label: 'Iaido (居合道)' },
                                { value: 'Kyudo', label: 'Kyudo (弓道)' },
                                { value: 'Jujutsu', label: 'Jujutsu (柔術)' },
                                { value: 'Ninjutsu', label: 'Ninjutsu (忍術)' },
                                { value: 'Sumo', label: 'Sumo (相撲)' },

                                { value: 'Jodo', label: 'Jodo (杖道)' },
                                { value: 'Naginata', label: 'Naginata (なぎなた)' },
                                { value: 'Shorinji Kempo', label: 'Shorinji Kempo (少林寺拳法)' },
                                { value: 'Other', label: 'Other (Custom)' }
                              ].map((art) => (
                                <label 
                                  key={art.value}
                                  className={`flex items-center space-x-2 cursor-pointer p-3 rounded-lg border-2 transition-all ${
                                    programForm.martial_arts_style === art.value
                                      ? 'border-emerald-500 bg-emerald-50'
                                      : 'border-slate-200 hover:border-slate-300 hover:bg-slate-100'
                                  }`}
                                >
                                  <input
                                    type="radio"
                                    name="martial_arts_style"
                                    value={art.value}
                                    checked={programForm.martial_arts_style === art.value}
                                    onChange={(e) => {
                                      setProgramForm({
                                        ...programForm,
                                        martial_arts_style: e.target.value,
                                        custom_martial_art: e.target.value === 'Other' ? programForm.custom_martial_art : ''
                                      });
                                    }}
                                    className="w-4 h-4 text-emerald-600 focus:ring-emerald-500"
                                  />
                                  <span className={`text-sm font-medium ${
                                    programForm.martial_arts_style === art.value ? 'text-slate-900' : 'text-slate-700'
                                  }`}>
                                    {art.label}
                                  </span>
                                </label>
                              ))}
                            </div>
                            
                            {/* Custom martial art input */}
                            {programForm.martial_arts_style === 'Other' && (
                              <div className="mt-4">
                                <Label>Specify Martial Art *</Label>
                                <Input 
                                  value={programForm.custom_martial_art}
                                  onChange={(e) => setProgramForm({...programForm, custom_martial_art: e.target.value})}
                                  placeholder="Enter martial art name"
                                  required
                                />
                              </div>
                            )}
                            
                            {/* KORYU DESIGNATION - Separate Option */}
                            <div className="mt-6 p-4 border-2 border-dashed border-amber-300 rounded-lg bg-gradient-to-br from-amber-50 to-orange-50">
                              <label className="flex items-start gap-4 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={programForm.is_koryu || false}
                                  onChange={(e) => setProgramForm({...programForm, is_koryu: e.target.checked})}
                                  className="w-5 h-5 mt-1 text-amber-600 border-amber-300 rounded focus:ring-amber-500"
                                />
                                <div className="flex-1">
                                  <div className="flex items-center gap-3 mb-2">
                                    <span className="text-lg font-bold text-amber-900">古流武術 (Koryu)</span>
                                    <span className="text-2xl">🎎</span>
                                  </div>
                                  <p className="text-sm text-amber-800 font-medium mb-1">
                                    Traditional Classical School
                                  </p>
                                  <p className="text-xs text-slate-700">
                                    Check this if your school teaches traditional pre-Meiji Restoration martial arts with authentic lineage and classical methods.
                                  </p>
                                  {programForm.is_koryu && (
                                    <div className="mt-3 flex items-center gap-3 p-3 bg-white/50 rounded border border-amber-200">
                                      <KoryuScrollBanner size="sm" />
                                      <span className="text-xs text-amber-900 font-medium">
                                        This scroll will appear on your course listing
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </label>
                            </div>
                          </div>


                          <div>
                            <Label>Class Type *</Label>
                            <Select 
                              value={programForm.class_type} 
                              onValueChange={(value) => setProgramForm({...programForm, class_type: value})}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="group">Group</SelectItem>
                                <SelectItem value="private">Private</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label>Price (JPY) *</Label>
                            <Input 
                              type="number"
                              value={programForm.price} 
                              onChange={(e) => setProgramForm({...programForm, price: e.target.value})}
                              required 
                            />
                          </div>
                          <div>
                            <Label>Capacity *</Label>
                            <Input 
                              type="number"
                              value={programForm.capacity} 
                              onChange={(e) => setProgramForm({...programForm, capacity: e.target.value})}
                              required 
                            />
                          </div>
                          <div>
                            <Label>Currency</Label>
                            <Input value="JPY" disabled />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Start Date *</Label>
                            <Input 
                              type="date"
                              value={programForm.start_date} 
                              onChange={(e) => setProgramForm({...programForm, start_date: e.target.value})}
                              required 
                            />
                          </div>
                          <div>
                            <Label>End Date *</Label>
                            <Input 
                              type="date"
                              value={programForm.end_date} 
                              onChange={(e) => setProgramForm({...programForm, end_date: e.target.value})}
                              required 
                            />
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label>Daily Start Time *</Label>
                            <Input 
                              type="time"
                              value={programForm.daily_start_time} 
                              onChange={(e) => setProgramForm({...programForm, daily_start_time: e.target.value})}
                              required 
                            />
                          </div>
                          <div>
                            <Label>Daily End Time *</Label>
                            <Input 
                              type="time"
                              value={programForm.daily_end_time} 
                              onChange={(e) => setProgramForm({...programForm, daily_end_time: e.target.value})}
                              required 
                            />
                          </div>
                        </div>
                        <div>
                          <Label>Program Image</Label>
                          <ImageUpload
                            onImageUploaded={(url) => setProgramForm({...programForm, image_url: url})}
                            currentImage={programForm.image_url}
                          />
                        </div>
                        <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800">
                          {editingProgram ? 'Update Program' : 'Create Program'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>

                {adminPrograms.length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <p className="text-slate-600">No programs yet. Create your first training program!</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {adminPrograms.map((program) => (
                      <Card key={program.id} className="overflow-hidden">
                        {program.image_url && (
                          <img src={program.image_url} alt={program.title} className="w-full h-40 object-cover" />
                        )}
                        <CardHeader>
                          <span className="text-xs font-semibold text-emerald-700 bg-emerald-100 px-3 py-1 rounded-full w-fit mb-2">
                            {program.martial_arts_style || program.category}
                          </span>
                          <CardTitle>{program.title}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-slate-600 mb-2">{program.description?.substring(0, 100)}...</p>
                          <p className="text-sm text-slate-600"><strong>Price:</strong> ¥{program.price?.toLocaleString('ja-JP')}</p>
                          <p className="text-sm text-slate-600"><strong>Capacity:</strong> {program.capacity} students</p>
                          <p className="text-sm text-slate-600"><strong>Level:</strong> {program.experience_level}</p>
                          <div className="flex gap-2 mt-4">
                            <Button 
                              onClick={() => handleEditProgram(program)} 
                              variant="outline" 
                              size="sm" 
                              className="flex-1"
                            >
                              Edit
                            </Button>
                            <button 
                              type="button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteProgram(program.id);
                              }}
                              className="flex-1 px-3 py-1.5 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 active:bg-red-800 transition-colors cursor-pointer"
                            >
                              Delete
                            </button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* Bookings Sub-tab */}
              <TabsContent value="bookings" className="mt-6">
                <h3 className="text-xl font-bold text-slate-900 mb-6">Bookings</h3>

                {adminBookings.length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <p className="text-slate-600">No bookings yet.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {adminBookings.map((booking) => (
                      <Card key={booking.id}>
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div>
                              <CardTitle className="text-lg">{booking.program_title || 'Program'}</CardTitle>
                              <CardDescription>{booking.student_name} ({booking.student_email})</CardDescription>
                            </div>
                            <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                              booking.status === 'paid' ? 'bg-green-100 text-green-700' : 
                              booking.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 
                              'bg-red-100 text-red-700'
                            }`}>
                              {booking.status}
                            </span>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                            <p className="text-slate-600"><strong>Amount:</strong> ¥{booking.price?.toLocaleString('ja-JP')}</p>
                            <p className="text-slate-600"><strong>Booked:</strong> {new Date(booking.created_at).toLocaleDateString()}</p>
                            <p className="text-slate-600"><strong>Booking ID:</strong> {booking.id?.substring(0, 8)}...</p>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </TabsContent>

          {/* Users Management Tab */}
          <TabsContent value="users" className="mt-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-base sm:text-lg md:text-xl font-bold text-slate-900" style={{ fontFamily: 'Spectral, serif' }}>User Management</h2>
              <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                <DialogTrigger asChild>
                  <Button 
                    className="bg-emerald-700 hover:bg-emerald-800"
                    onClick={() => {
                      setEditingUser(null);
                      setUserForm({ name: '', email: '', password: '', role: 'student', phone: '' });
                    }}
                  >
                    + Add User
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>{editingUser ? 'Edit User' : 'Create New User'}</DialogTitle>
                    <DialogDescription>
                      {editingUser ? 'Update user information' : 'Add a new user to the platform'}
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleCreateUser} className="space-y-4">
                    <div>
                      <Label>Name *</Label>
                      <Input 
                        value={userForm.name} 
                        onChange={(e) => setUserForm({...userForm, name: e.target.value})}
                        required 
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input 
                        type="email"
                        value={userForm.email} 
                        onChange={(e) => setUserForm({...userForm, email: e.target.value})}
                        required 
                      />
                    </div>
                    <div>
                      <Label>{editingUser ? 'New Password (optional)' : 'Password *'}</Label>
                      <Input 
                        type="password"
                        value={userForm.password} 
                        onChange={(e) => setUserForm({...userForm, password: e.target.value})}
                        required={!editingUser}
                        placeholder={editingUser ? "Leave blank to keep current password" : "Min 6 characters"}
                      />
                      {editingUser && (
                        <p className="text-xs text-slate-500 mt-1">
                          Leave blank to keep the current password. Enter a new password to change it.
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>Role *</Label>
                      <select 
                        className="w-full px-3 py-2 border border-slate-300 rounded-md"
                        value={userForm.role}
                        onChange={(e) => setUserForm({...userForm, role: e.target.value})}
                      >
                        <option value="student">Student</option>
                        <option value="school">School</option>
                        <option value="admin">Admin</option>
                        <option value="instructor">Instructor</option>
                      </select>
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input 
                        type="tel"
                        value={userForm.phone} 
                        onChange={(e) => setUserForm({...userForm, phone: e.target.value})}
                      />
                    </div>
                    
                    {/* School selection for instructor role */}
                    {userForm.role === 'instructor' && (
                      <div>
                        <Label>School (for Instructor) *</Label>
                        <select 
                          className="w-full px-3 py-2 border border-slate-300 rounded-md"
                          value={userForm.school_id}
                          onChange={(e) => setUserForm({...userForm, school_id: e.target.value})}
                          required={userForm.role === 'instructor'}
                        >
                          <option value="">Select a school...</option>
                          {schools.map(school => (
                            <option key={school.id} value={school.id}>
                              {school.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    <Button type="submit" className="w-full bg-emerald-700 hover:bg-emerald-800">
                      {editingUser ? 'Update User' : 'Create User'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Search Bar */}
            <div className="mb-6">
              <Input 
                placeholder="Search users by name, email, or role..." 
                value={userSearchQuery}
                onChange={(e) => setUserSearchQuery(e.target.value)}
                className="max-w-md"
              />
            </div>

            {/* Users Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto" style={{ position: 'relative', zIndex: 1 }}>
                  <table className="w-full" style={{ position: 'relative' }}>
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="text-left p-4 text-sm font-semibold text-slate-700">Name</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-700">Email</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-700">Role</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-700">Phone</th>
                        <th className="text-left p-4 text-sm font-semibold text-slate-700">Created</th>
                        <th className="text-right p-4 text-sm font-semibold text-slate-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {filteredUsers.length === 0 ? (
                        <tr>
                          <td colSpan="6" className="p-8 text-center text-slate-600">
                            {userSearchQuery ? 'No users found matching your search.' : 'No users yet.'}
                          </td>
                        </tr>
                      ) : (
                        filteredUsers.map((userRow) => (
                          <tr key={userRow.id} className="hover:bg-slate-50">
                            <td className="p-4">
                              <div className="flex items-center gap-3">
                                <img 
                                  src={userRow.picture} 
                                  alt={userRow.name}
                                  className="w-10 h-10 rounded-full"
                                />
                                <span className="font-medium text-slate-900">{userRow.name}</span>
                              </div>
                            </td>
                            <td className="p-4 text-sm text-slate-600">{userRow.email}</td>
                            <td className="p-4">
                              <span className={`text-xs font-semibold px-3 py-1 rounded-full ${
                                userRow.role === 'admin' ? 'bg-purple-100 text-purple-700' : 
                                userRow.role === 'school' ? 'bg-blue-100 text-blue-700' : 
                                userRow.role === 'instructor' ? 'bg-emerald-100 text-emerald-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {userRow.role}
                              </span>
                            </td>
                            <td className="p-4 text-sm text-slate-600">{userRow.phone || '-'}</td>
                            <td className="p-4 text-sm text-slate-600">
                              {new Date(userRow.created_at).toLocaleDateString()}
                            </td>
                            <td className="p-4">
                              <div className="flex items-center justify-end gap-2">
                                <button 
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleEditUser(userRow);
                                  }}
                                  className="px-3 py-1.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 active:bg-slate-100 transition-colors cursor-pointer z-10 relative"
                                  style={{ pointerEvents: 'auto' }}
                                >
                                  Edit
                                </button>
                                {userRow.id === user?.id ? (
                                  <button 
                                    type="button"
                                    disabled
                                    title="Cannot delete your own account"
                                    className="px-3 py-1.5 text-sm font-medium text-slate-400 bg-slate-100 border border-slate-300 rounded-md cursor-not-allowed z-10 relative"
                                  >
                                    Delete
                                  </button>
                                ) : (
                                  <button 
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleDeleteUser(userRow.id, userRow.name);
                                    }}
                                    className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 border border-red-600 rounded-md hover:bg-red-700 active:bg-red-800 transition-colors cursor-pointer z-10 relative"
                                    style={{ pointerEvents: 'auto' }}
                                  >
                                    Delete
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* User Statistics */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-slate-600">Total Users</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-slate-900">{users.length}</p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-slate-600">Admins</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-purple-700">
                    {users.filter(u => u.role === 'admin').length}
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-slate-600">Schools</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-blue-700">
                    {users.filter(u => u.role === 'school').length}
                  </p>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* BRANDING TAB */}


          {/* AI Knowledge Base Tab */}
          <TabsContent value="knowledge" className="space-y-6">

            {/* Google Drive Sync */}
            <Card className="mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <svg className="w-5 h-5 text-blue-500" viewBox="0 0 24 24" fill="currentColor"><path d="M7.71 3.5L1.15 15l4.58 6.5h2.85L3.44 15l5.13-8.5H7.71zm8.58 0l-5.13 8.5L16.3 21.5h2.85L13.43 12l6.57-8.5h-3.71zm-4.29 0L5.43 15l2.85 4.5h10.44L21.57 15 15 3.5H12z"/></svg>
                      Google Drive Auto-Sync
                    </CardTitle>
                    <CardDescription>
                      Files from your shared Drive folder are automatically synced every 24 hours
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={previewDriveFiles}>Preview Files</Button>
                    <Button
                      data-testid="sync-drive-btn"
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700"
                      onClick={triggerDriveSync}
                      disabled={driveSyncing}
                    >
                      {driveSyncing ? 'Syncing...' : 'Sync Now'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {driveStatus ? (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                      <div className="p-2.5 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Connection</p>
                        <p className={`font-semibold ${driveStatus.connection_ok ? 'text-emerald-600' : 'text-red-500'}`}>
                          {driveStatus.connection_ok ? 'Connected' : 'Error'}
                        </p>
                      </div>
                      <div className="p-2.5 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Files in Folder</p>
                        <p className="font-semibold text-blue-600">{driveStatus.drive_files_found}</p>
                      </div>
                      <div className="p-2.5 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Drive Articles</p>
                        <p className="font-semibold text-blue-600">{driveStatus.drive_articles}</p>
                      </div>
                      <div className="p-2.5 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Manual Articles</p>
                        <p className="font-semibold text-slate-700">{driveStatus.manual_articles}</p>
                      </div>
                    </div>
                    {driveStatus.folder_url && (
                      <p className="text-xs text-slate-500">
                        Folder: <a href={driveStatus.folder_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{driveStatus.folder_url}</a>
                      </p>
                    )}
                    {driveStatus.connection_error && (
                      <p className="text-xs text-red-500 bg-red-50 p-2 rounded">Error: {driveStatus.connection_error}</p>
                    )}
                    {driveStatus.last_sync && (
                      <p className="text-xs text-slate-400">
                        Last sync: {new Date(driveStatus.last_sync.timestamp).toLocaleString()} — {driveStatus.last_sync.synced} synced, {driveStatus.last_sync.skipped} unchanged
                        {driveStatus.last_sync.errors?.length > 0 && `, ${driveStatus.last_sync.errors.length} errors`}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500">Loading Drive status...</p>
                )}

                {/* Drive Files Preview */}
                {driveFiles && (
                  <div className="mt-4 border-t border-slate-100 pt-3">
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">Files in Drive Folder ({driveFiles.length})</h4>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                      {driveFiles.map(f => (
                        <div key={f.id} className="flex items-center justify-between text-xs p-1.5 hover:bg-slate-50 rounded">
                          <div className="flex items-center gap-2">
                            <span className={`px-1.5 py-0.5 rounded text-xs ${
                              f.mimeType.includes('document') ? 'bg-blue-100 text-blue-700' :
                              f.mimeType.includes('spreadsheet') ? 'bg-green-100 text-green-700' :
                              f.mimeType.includes('presentation') ? 'bg-orange-100 text-orange-700' :
                              f.mimeType.includes('pdf') ? 'bg-red-100 text-red-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {f.mimeType.includes('document') ? 'Doc' :
                               f.mimeType.includes('spreadsheet') ? 'Sheet' :
                               f.mimeType.includes('presentation') ? 'Slides' :
                               f.mimeType.includes('pdf') ? 'PDF' : 'File'}
                            </span>
                            <span className="text-slate-700">{f.name}</span>
                          </div>
                          <span className="text-slate-400">{new Date(f.modifiedTime).toLocaleDateString()}</span>
                        </div>
                      ))}
                    </div>
                    <Button variant="ghost" size="sm" className="mt-2 text-xs" onClick={() => setDriveFiles(null)}>Close preview</Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">AI Knowledge Base</CardTitle>
                    <CardDescription>Train the AI assistant with your content. Paste from Google Docs, add FAQs, school policies, etc.</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={fetchKnowledge} disabled={knowledgeLoading}>
                      {knowledgeLoading ? 'Loading...' : 'Refresh'}
                    </Button>
                    <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => { setShowKnowledgeForm(true); setEditingArticle(null); setNewArticle({ title: '', content: '', category: 'general', source_url: '' }); }}>
                      + Add Article
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Add/Edit Form */}
                {showKnowledgeForm && (
                  <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200 space-y-4">
                    <h4 className="font-semibold text-sm text-slate-700">{editingArticle ? 'Edit Article' : 'Add New Knowledge Article'}</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label>Title</Label>
                        <Input
                          data-testid="knowledge-title-input"
                          value={newArticle.title}
                          onChange={e => setNewArticle({...newArticle, title: e.target.value})}
                          placeholder="e.g. Booking Policy, Refund FAQ, Karate Styles Guide"
                        />
                      </div>
                      <div>
                        <Label>Category</Label>
                        <Select value={newArticle.category} onValueChange={v => setNewArticle({...newArticle, category: v})}>
                          <SelectTrigger data-testid="knowledge-category-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="general">General</SelectItem>
                            <SelectItem value="faq">FAQ</SelectItem>
                            <SelectItem value="policy">Policy</SelectItem>
                            <SelectItem value="martial_arts">Martial Arts Info</SelectItem>
                            <SelectItem value="school_info">School Information</SelectItem>
                            <SelectItem value="pricing">Pricing & Payments</SelectItem>
                            <SelectItem value="location">Location & Travel</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Source URL (optional - Google Doc link, etc.)</Label>
                      <Input
                        data-testid="knowledge-source-input"
                        value={newArticle.source_url}
                        onChange={e => setNewArticle({...newArticle, source_url: e.target.value})}
                        placeholder="https://docs.google.com/document/d/..."
                      />
                    </div>
                    <div>
                      <Label>Content (paste your Google Doc content, FAQ text, policies, etc.)</Label>
                      <Textarea
                        data-testid="knowledge-content-input"
                        value={newArticle.content}
                        onChange={e => setNewArticle({...newArticle, content: e.target.value})}
                        placeholder="Paste your content here. The AI will use this to answer questions from schools and students.&#10;&#10;Tip: Copy and paste directly from Google Docs - formatting will be preserved as text."
                        rows={8}
                        className="font-mono text-sm"
                      />
                      <p className="text-xs text-slate-400 mt-1">{newArticle.content.length.toLocaleString()} characters</p>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" size="sm" onClick={() => { setShowKnowledgeForm(false); setEditingArticle(null); }}>Cancel</Button>
                      <Button data-testid="save-knowledge-btn" size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={saveKnowledgeArticle}>
                        {editingArticle ? 'Update Article' : 'Save to Knowledge Base'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Articles List */}
                {knowledgeArticles.length > 0 ? (
                  <div className="space-y-3">
                    {knowledgeArticles.map(article => (
                      <div key={article.id} className={`p-4 rounded-lg border ${article.active ? 'bg-white border-slate-200' : 'bg-slate-50 border-slate-100 opacity-60'}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h4 className="font-medium text-slate-800 text-sm">{article.title}</h4>
                              <span className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-xs">{article.category}</span>
                              {article.active ? (
                                <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs">Active</span>
                              ) : (
                                <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-600 text-xs">Inactive</span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 line-clamp-2">{article.content.substring(0, 200)}...</p>
                            <div className="flex items-center gap-3 mt-2 text-xs text-slate-400">
                              <span>{article.char_count?.toLocaleString()} chars</span>
                              {article.source_url && <a href={article.source_url} target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:underline">Source</a>}
                              <span>{new Date(article.created_at).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => toggleKnowledgeActive(article.id, article.active)}>
                              {article.active ? 'Disable' : 'Enable'}
                            </Button>
                            <Button variant="ghost" size="sm" className="h-8 text-xs" onClick={() => {
                              setEditingArticle(article);
                              setNewArticle({ title: article.title, content: article.content, category: article.category, source_url: article.source_url || '' });
                              setShowKnowledgeForm(true);
                            }}>Edit</Button>
                            <Button variant="ghost" size="sm" className="h-8 text-xs text-red-500 hover:text-red-700" onClick={() => deleteKnowledgeArticle(article.id)}>Delete</Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <div className="text-4xl mb-3">🧠</div>
                    <p className="text-sm font-medium">No knowledge articles yet</p>
                    <p className="text-xs mt-1">Add content from your Google Docs to train the AI assistant</p>
                    <Button size="sm" className="mt-3 bg-emerald-600 hover:bg-emerald-700" onClick={() => {
                      setShowKnowledgeForm(true);
                      fetchKnowledge();
                    }}>Add Your First Article</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Financial Tab */}
          <TabsContent value="financial" className="space-y-6">
            <div className="mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-base sm:text-lg md:text-xl font-bold text-slate-900">Financial Overview</h2>
                  <p className="text-slate-600 text-sm">Revenue, fees, and earnings breakdown</p>
                </div>
                <ExchangeRateDisplay size="sm" />
              </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card className="border-2 border-green-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <span className="text-2xl">💵</span>
                    Total Revenue
                  </CardTitle>
                  <CardDescription>All paid bookings combined</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-green-600">
                    ¥{stats?.total_revenue ? stats.total_revenue.toLocaleString('ja-JP') : '0'}
                  </p>
                  <p className="text-sm text-slate-600 mt-2">From {stats?.paid_bookings || 0} paid bookings</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-emerald-200">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <span className="text-2xl">💰</span>
                    Platform Revenue
                  </CardTitle>
                  <CardDescription>Booking fees collected (9%)</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl sm:text-3xl md:text-4xl font-bold text-emerald-600">
                    ¥{stats?.total_booking_fees ? stats.total_booking_fees.toLocaleString('ja-JP') : '0'}
                  </p>
                  <p className="text-sm text-slate-600 mt-2">9% platform fee on each booking</p>
                </CardContent>
              </Card>
            </div>

            {/* Detailed Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Revenue Breakdown</CardTitle>
                <CardDescription>How the money is distributed</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Total Revenue Bar */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">Total Revenue</span>
                    <span className="text-sm font-bold text-green-600">
                      ¥{stats?.total_revenue ? stats.total_revenue.toLocaleString('ja-JP') : '0'}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    <div className="bg-green-500 h-3 rounded-full" style={{ width: '100%' }}></div>
                  </div>
                </div>

                {/* Platform Fees */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">Platform Fees (9%)</span>
                    <span className="text-sm font-bold text-emerald-600">
                      ¥{stats?.total_booking_fees ? stats.total_booking_fees.toLocaleString('ja-JP') : '0'}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    <div className="bg-emerald-500 h-3 rounded-full" 
                      style={{ width: stats?.total_revenue ? `${(stats.total_booking_fees / stats.total_revenue * 100)}%` : '0%' }}
                    ></div>
                  </div>
                </div>

                {/* Sales Tax */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">Sales Tax Collected (10%)</span>
                    <span className="text-sm font-bold text-blue-600">
                      ¥{stats?.total_sales_tax ? stats.total_sales_tax.toLocaleString('ja-JP') : '0'}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    <div className="bg-blue-500 h-3 rounded-full" 
                      style={{ width: stats?.total_revenue ? `${(stats.total_sales_tax / stats.total_revenue * 100)}%` : '0%' }}
                    ></div>
                  </div>
                </div>

                {/* School Earnings */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-slate-700">School Earnings</span>
                    <span className="text-sm font-bold text-purple-600">
                      ¥{stats?.total_school_earnings ? stats.total_school_earnings.toLocaleString('ja-JP') : '0'}
                    </span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-3">
                    <div className="bg-purple-500 h-3 rounded-full" 
                      style={{ width: stats?.total_revenue ? `${(stats.total_school_earnings / stats.total_revenue * 100)}%` : '0%' }}
                    ></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Booking Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-slate-600">Total Bookings</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-slate-900">{stats?.total_bookings || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">All bookings</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-slate-600">Paid Bookings</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-green-600">{stats?.paid_bookings || 0}</p>
                  <p className="text-xs text-slate-500 mt-1">Completed payments</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-slate-600">Conversion Rate</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-blue-600">
                    {stats?.total_bookings ? Math.round((stats.paid_bookings / stats.total_bookings) * 100) : 0}%
                  </p>
                  <p className="text-xs text-slate-500 mt-1">Bookings → Paid</p>
                </CardContent>
              </Card>
            </div>

            {/* Quick Summary */}
            <Card className="bg-gradient-to-r from-emerald-50 to-green-50 border-emerald-200">
              <CardContent className="pt-6">
                <h3 className="font-semibold text-lg mb-4">Financial Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-slate-600">Total money processed through platform:</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      ¥{stats?.total_revenue ? stats.total_revenue.toLocaleString('ja-JP') : '0'}
                    </p>
                  </div>
                  <div>
                    <p className="text-slate-600">Your platform earnings (9% + tax):</p>
                    <p className="text-2xl font-bold text-emerald-600 mt-1">
                      ¥{stats?.total_booking_fees && stats?.total_sales_tax 
                        ? (stats.total_booking_fees + stats.total_sales_tax).toLocaleString('ja-JP') 
                        : '0'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stripe Platform Balance */}
            {stripeBalance && (
              <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                <CardContent className="pt-6">
                  <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                    <span className="text-2xl">💳</span> Stripe Platform Balance
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-slate-600">Available</p>
                      {stripeBalance.available?.map((b, i) => (
                        <p key={i} className="text-xl font-bold text-green-600">
                          {b.currency === 'jpy' ? '¥' : b.currency.toUpperCase() + ' '}{b.amount?.toLocaleString()}
                        </p>
                      ))}
                    </div>
                    <div>
                      <p className="text-sm text-slate-600">Pending</p>
                      {stripeBalance.pending?.map((b, i) => (
                        <p key={i} className="text-xl font-bold text-amber-600">
                          {b.currency === 'jpy' ? '¥' : b.currency.toUpperCase() + ' '}{b.amount?.toLocaleString()}
                        </p>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* School Payouts Management */}
            <Card className="mt-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">School Payouts</CardTitle>
                    <CardDescription>Per-school earnings and payout tracking (Stripe-sourced)</CardDescription>
                  </div>
                  <Button variant="outline" size="sm" onClick={fetchPayoutSummary} disabled={payoutLoading}>
                    {payoutLoading ? 'Loading...' : 'Refresh'}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {payoutSummary ? (
                  <div className="space-y-4">
                    {/* Totals Row */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-3 bg-slate-50 rounded-lg text-sm">
                      <div><p className="text-xs text-slate-500">Total School Earnings</p><p className="font-bold text-emerald-600">&yen;{payoutSummary.totals.school_earnings?.toLocaleString('ja-JP') || '0'}</p></div>
                      <div><p className="text-xs text-slate-500">Paid Out</p><p className="font-bold text-green-600">&yen;{payoutSummary.totals.paid_out?.toLocaleString('ja-JP') || '0'}</p></div>
                      <div><p className="text-xs text-slate-500">Pending</p><p className="font-bold text-amber-600">&yen;{payoutSummary.totals.pending_payouts?.toLocaleString('ja-JP') || '0'}</p></div>
                      <div><p className="text-xs text-slate-500">Available</p><p className="font-bold text-blue-600">&yen;{payoutSummary.totals.available_balance?.toLocaleString('ja-JP') || '0'}</p></div>
                    </div>

                    {/* Per-School Table */}
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200 text-left">
                            <th className="py-2 px-3 font-semibold text-slate-700">School</th>
                            <th className="py-2 px-3 font-semibold text-slate-700 text-right">Earnings</th>
                            <th className="py-2 px-3 font-semibold text-slate-700 text-right">Paid</th>
                            <th className="py-2 px-3 font-semibold text-slate-700 text-right">Pending</th>
                            <th className="py-2 px-3 font-semibold text-slate-700 text-right">Available</th>
                            <th className="py-2 px-3 font-semibold text-slate-700">Payouts</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payoutSummary.schools.map(school => (
                            <tr key={school.school_id} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-2.5 px-3">
                                <p className="font-medium text-slate-800">{school.school_name}</p>
                                <p className="text-xs text-slate-500">{school.contact_email}</p>
                              </td>
                              <td className="py-2.5 px-3 text-right font-medium text-emerald-600">&yen;{school.school_earnings?.toLocaleString('ja-JP') || '0'}</td>
                              <td className="py-2.5 px-3 text-right text-green-600">&yen;{school.paid_out?.toLocaleString('ja-JP') || '0'}</td>
                              <td className="py-2.5 px-3 text-right text-amber-600">&yen;{school.pending_payouts?.toLocaleString('ja-JP') || '0'}</td>
                              <td className="py-2.5 px-3 text-right font-medium text-blue-600">&yen;{school.available_balance?.toLocaleString('ja-JP') || '0'}</td>
                              <td className="py-2.5 px-3">
                                {school.payout_requests.length > 0 ? (
                                  <div className="space-y-1">
                                    {school.payout_requests.map(pr => (
                                      <div key={pr.id} className="flex items-center gap-2">
                                        <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                                          pr.status === 'paid' ? 'bg-green-100 text-green-700' :
                                          pr.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                                          pr.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                          'bg-blue-100 text-blue-700'
                                        }`}>{pr.status}</span>
                                        <span className="text-xs text-slate-600">&yen;{pr.amount?.toLocaleString('ja-JP')}</span>
                                        {pr.status === 'pending' && (
                                          <>
                                            <button
                                              data-testid={`process-stripe-${pr.id}`}
                                              onClick={() => handleProcessStripePayout(pr.id)}
                                              disabled={processingPayout === pr.id}
                                              className="text-xs text-blue-600 hover:text-blue-800 underline disabled:opacity-50"
                                            >{processingPayout === pr.id ? 'Processing...' : 'Auto-Pay'}</button>
                                            <button
                                              data-testid={`record-payment-${pr.id}`}
                                              onClick={() => setPayoutDialog(pr)}
                                              className="text-xs text-emerald-600 hover:text-emerald-800 underline"
                                            >Manual</button>
                                          </>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-xs text-slate-400">No payouts</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">
                    {payoutLoading ? 'Loading payout data...' : 'Click Refresh to load payout data'}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Record Payment Dialog */}
            {payoutDialog && (
              <Dialog open={!!payoutDialog} onOpenChange={() => setPayoutDialog(null)}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Record Payment</DialogTitle>
                    <DialogDescription>
                      Mark payout of &yen;{payoutDialog.amount?.toLocaleString('ja-JP')} to {payoutDialog.school_name} as paid
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-2">
                    <div>
                      <Label>Payout Method</Label>
                      <Select value={payoutForm.payout_method} onValueChange={v => setPayoutForm({...payoutForm, payout_method: v})}>
                        <SelectTrigger data-testid="payout-method-select"><SelectValue placeholder="Select method..." /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                          <SelectItem value="stripe">Stripe</SelectItem>
                          <SelectItem value="paypal">PayPal</SelectItem>
                          <SelectItem value="cash">Cash</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Transaction Reference / ID</Label>
                      <Input
                        data-testid="transaction-reference-input"
                        value={payoutForm.transaction_reference}
                        onChange={e => setPayoutForm({...payoutForm, transaction_reference: e.target.value})}
                        placeholder="e.g. Stripe transfer ID, bank ref number"
                      />
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Textarea
                        data-testid="payout-notes-input"
                        value={payoutForm.admin_notes}
                        onChange={e => setPayoutForm({...payoutForm, admin_notes: e.target.value})}
                        placeholder="Any notes about this payment..."
                        rows={2}
                      />
                    </div>
                    <div className="flex gap-3 justify-end">
                      <Button variant="outline" onClick={() => setPayoutDialog(null)}>Cancel</Button>
                      <Button variant="destructive" onClick={() => handleMarkPayout(payoutDialog.id, 'rejected')}>Reject</Button>
                      <Button data-testid="confirm-payment-btn" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => handleMarkPayout(payoutDialog.id, 'paid')}>Mark as Paid</Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            )}
          </TabsContent>


          {/* Chatbot Analytics Tab */}
          <TabsContent value="chatbot" className="space-y-6">
            <ChatbotAnalyticsTab />
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics" className="space-y-6">

            {/* Conversion Funnel */}
            <Card className="mb-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-lg">Conversion Funnel</CardTitle>
                    <CardDescription>Track where visitors drop off in the booking journey</CardDescription>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => fetchFunnel()}>Refresh</Button>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => fetchBehaviorReport()} disabled={reportLoading}>
                      {reportLoading ? 'Analyzing...' : 'AI Report'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {funnelData ? (
                  <div className="space-y-4">
                    {/* Funnel Steps */}
                    <div className="space-y-1">
                      {[
                        { label: 'Sessions', value: funnelData.funnel.sessions, color: 'bg-slate-600' },
                        { label: 'Page Views', value: funnelData.funnel.page_views, color: 'bg-blue-500' },
                        { label: 'Course Views', value: funnelData.funnel.course_views, color: 'bg-emerald-500' },
                        { label: 'Course Clicks', value: funnelData.funnel.course_clicks, color: 'bg-teal-500' },
                        { label: 'Book Clicks', value: funnelData.funnel.book_clicks, color: 'bg-amber-500' },
                        { label: 'Enquiries', value: funnelData.funnel.enquiry_clicks + funnelData.funnel.contact_messages, color: 'bg-orange-500' },
                        { label: 'Bookings', value: funnelData.funnel.bookings, color: 'bg-red-500' },
                      ].map((step, i) => {
                        const maxVal = funnelData.funnel.sessions || 1;
                        const pct = Math.max((step.value / maxVal) * 100, step.value > 0 ? 3 : 0);
                        return (
                          <div key={step.label} className="flex items-center gap-3">
                            <span className="text-xs text-slate-500 w-24 text-right">{step.label}</span>
                            <div className="flex-1 h-7 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-full ${step.color} rounded-full flex items-center px-2 transition-all duration-500`} style={{ width: `${pct}%`, minWidth: step.value > 0 ? '40px' : '0' }}>
                                <span className="text-xs font-bold text-white">{step.value}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Engagement Metrics */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-4 pt-4 border-t">
                      <div className="text-center p-2 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Avg Time</p>
                        <p className="text-sm font-bold text-slate-800">{funnelData.engagement.avg_duration_display}</p>
                      </div>
                      <div className="text-center p-2 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Bounce Rate</p>
                        <p className="text-sm font-bold text-slate-800">{funnelData.engagement.bounce_rate}%</p>
                      </div>
                      <div className="text-center p-2 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Total Clicks</p>
                        <p className="text-sm font-bold text-slate-800">{funnelData.engagement.total_clicks}</p>
                      </div>
                      <div className="text-center p-2 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Conv. Rate</p>
                        <p className="text-sm font-bold text-slate-800">{funnelData.funnel.sessions > 0 ? ((funnelData.funnel.bookings / funnelData.funnel.sessions) * 100).toFixed(1) : 0}%</p>
                      </div>
                    </div>

                    {/* Top Pages */}
                    {funnelData.top_pages?.length > 0 && (
                      <div className="mt-4 pt-4 border-t">
                        <h4 className="text-sm font-semibold text-slate-700 mb-2">Top Pages</h4>
                        <div className="space-y-1">
                          {funnelData.top_pages.slice(0, 8).map(p => (
                            <div key={p.path} className="flex items-center justify-between text-xs">
                              <span className="text-slate-600 truncate">{p.path}</span>
                              <span className="text-slate-800 font-medium ml-2">{p.views}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-slate-500 text-center py-4">Loading funnel data...</p>
                )}

                {/* AI Behavior Report */}
                {behaviorReport && (
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="text-sm font-semibold text-slate-700 mb-2">AI Behavior Analysis</h4>
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-sm text-slate-700 whitespace-pre-wrap max-h-64 overflow-y-auto"
                      dangerouslySetInnerHTML={{ __html: behaviorReport.report?.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>').replace(/\n/g, '<br/>') || 'No report generated' }}
                    />
                    <p className="text-xs text-slate-400 mt-1">Generated: {new Date(behaviorReport.generated_at).toLocaleString()}</p>
                  </div>
                )}
              </CardContent>
            </Card>


            <div className="flex justify-between items-center mb-6">
              <div>
                <h2 className="text-base sm:text-lg md:text-xl font-bold text-slate-900">Website Analytics</h2>
                <p className="text-slate-600 text-sm">Traffic, visitors, and engagement metrics</p>
              </div>
              <div className="flex gap-3 items-center">
                <div className="flex gap-2">
                  <Button
                    data-testid="download-pdf-btn"
                    onClick={() => {
                      const days = analyticsDays || 30;
                      const end = new Date(); const start = new Date(); start.setDate(start.getDate() - days);
                      const fmt = d => d.toISOString().slice(0,10);
                      window.open(`${API}/analytics/report?format=pdf&start_date=${fmt(start)}&end_date=${fmt(end)}`, '_blank');
                    }}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    PDF
                  </Button>
                  <Button
                    data-testid="download-csv-btn"
                    onClick={() => {
                      const days = analyticsDays || 30;
                      const end = new Date(); const start = new Date(); start.setDate(start.getDate() - days);
                      const fmt = d => d.toISOString().slice(0,10);
                      window.open(`${API}/analytics/report?format=csv&start_date=${fmt(start)}&end_date=${fmt(end)}`, '_blank');
                    }}
                    variant="outline"
                    className="border-emerald-600 text-emerald-700 hover:bg-emerald-50"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    CSV
                  </Button>
                </div>
              <div className="flex gap-3 items-center">
                <Select value={analyticsDays.toString()} onValueChange={(val) => {
                  setAnalyticsDays(parseInt(val));
                  fetchAnalytics(parseInt(val));
                }}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">Last 7 days</SelectItem>
                    <SelectItem value="30">Last 30 days</SelectItem>
                    <SelectItem value="90">Last 90 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              </div>
            </div>

            {analyticsLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600"></div>
              </div>
            ) : analyticsData ? (
              <>
                {/* Real-time Visitors */}
                {realtimeData && (
                  <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                    <CardContent className="pt-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-green-700 font-medium">Active Visitors (Last 5 min)</p>
                          <p className="text-3xl font-bold text-green-600 mt-1">{realtimeData.active_visitors || 0}</p>
                        </div>
                        <div className="h-3 w-3 bg-green-500 rounded-full animate-pulse"></div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Overview Cards */}
                <div className="grid md:grid-cols-4 gap-4">
                  <Card className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">Page Views</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-slate-900">{analyticsData.total_page_views.toLocaleString()}</p>
                      <p className="text-xs text-slate-500 mt-1">Total views</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">Unique Visitors</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-emerald-600">{analyticsData.unique_visitors.toLocaleString()}</p>
                      <p className="text-xs text-slate-500 mt-1">Distinct users</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">Sessions</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-blue-600">{analyticsData.total_sessions.toLocaleString()}</p>
                      <p className="text-xs text-slate-500 mt-1">Total visits</p>
                    </CardContent>
                  </Card>
                  
                  <Card className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-medium text-slate-600">Avg Session</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-3xl font-bold text-purple-600">{Math.floor(analyticsData.avg_session_duration / 60)}m {analyticsData.avg_session_duration % 60}s</p>
                      <p className="text-xs text-slate-500 mt-1">Duration</p>
                    </CardContent>
                  </Card>
                </div>

                {/* Financial Summary - Small Text Format */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Financial Overview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Total Revenue</p>
                        <p className="text-lg font-semibold text-green-600 mt-1">¥{stats?.total_revenue?.toLocaleString('ja-JP') || '0'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Platform Fees (9%)</p>
                        <p className="text-lg font-semibold text-emerald-600 mt-1">¥{stats?.total_booking_fees?.toLocaleString('ja-JP') || '0'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">Sales Tax (10%)</p>
                        <p className="text-lg font-semibold text-blue-600 mt-1">¥{stats?.total_sales_tax?.toLocaleString('ja-JP') || '0'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase tracking-wide">School Earnings</p>
                        <p className="text-lg font-semibold text-purple-600 mt-1">¥{stats?.total_school_earnings?.toLocaleString('ja-JP') || '0'}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Traffic Sources & Top Pages */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Traffic Sources</CardTitle>
                      <CardDescription>Where visitors come from</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {analyticsData.traffic_sources.map((source, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-2xl">
                                {source.source === 'direct' ? '🔗' : 
                                 source.source === 'google' ? '🔍' :
                                 source.source === 'facebook' ? '📘' :
                                 source.source === 'referral' ? '↗️' : '🌐'}
                              </span>
                              <span className="text-sm font-medium capitalize">{source.source || 'Unknown'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm text-slate-600">{source.count}</span>
                              <div className="w-20 bg-slate-200 rounded-full h-2">
                                <div 
                                  className="bg-emerald-500 h-2 rounded-full" 
                                  style={{ width: `${(source.count / analyticsData.traffic_sources[0].count) * 100}%` }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Top Pages</CardTitle>
                      <CardDescription>Most visited pages</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {analyticsData.top_pages.slice(0, 5).map((page, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <div className="flex-1 min-w-0 mr-3">
                              <p className="text-sm font-medium truncate">{page.title || page.url}</p>
                              <p className="text-xs text-slate-500 truncate">{page.url}</p>
                            </div>
                            <span className="text-sm font-semibold text-emerald-600">{page.views} views</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Geographic Data & Devices */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Top Countries</CardTitle>
                      <CardDescription>Visitors by location</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {analyticsData.top_countries.length > 0 ? analyticsData.top_countries.map((country, idx) => (
                          <div key={idx} className="flex items-center justify-between py-2 border-b last:border-0">
                            <span className="text-sm font-medium">{country.country || 'Unknown'}</span>
                            <span className="text-sm text-slate-600">{country.visitors} visitors</span>
                          </div>
                        )) : (
                          <p className="text-sm text-slate-500">No geographic data available yet</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Device Breakdown</CardTitle>
                      <CardDescription>How visitors access the site</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {analyticsData.device_breakdown.map((device, idx) => (
                          <div key={idx} className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <span className="text-xl">
                                {device.device === 'desktop' ? '💻' :
                                 device.device === 'mobile' ? '📱' :
                                 device.device === 'tablet' ? '📱' : '🖥️'}
                              </span>
                              <span className="text-sm font-medium capitalize">{device.device || 'Unknown'}</span>
                            </div>
                            <span className="text-sm text-slate-600">{device.count}</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Daily Views Chart */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Daily Page Views</CardTitle>
                    <CardDescription>Trend over the selected period</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {analyticsData.daily_views.length > 0 ? (
                      <div className="space-y-2">
                        {analyticsData.daily_views.slice(-14).map((day, idx) => (
                          <div key={idx} className="flex items-center gap-3">
                            <span className="text-xs text-slate-500 w-24">{day.date}</span>
                            <div className="flex-1 bg-slate-200 rounded-full h-6 relative">
                              <div 
                                className="bg-emerald-500 h-6 rounded-full flex items-center justify-end pr-2" 
                                style={{ 
                                  width: `${(day.views / Math.max(...analyticsData.daily_views.map(d => d.views))) * 100}%`,
                                  minWidth: '30px'
                                }}
                              >
                                <span className="text-xs font-medium text-white">{day.views}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-slate-500">No data available for the selected period</p>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : (
              <Card>
                <CardContent className="pt-6">
                  <p className="text-center text-slate-500">No analytics data available. Start tracking visitors to see insights here.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>


          <TabsContent value="branding" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Site-Wide Branding</CardTitle>
                <CardDescription>Customize the appearance and branding of your site</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Site Name & Logo */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label>Site Name</Label>
                    <Input
                      value={brandingData.site_name}
                      onChange={(e) => setBrandingData({...brandingData, site_name: e.target.value})}
                      placeholder="Train In Japan"
                    />
                  </div>
                  <div>
                    <Label>Primary Color</Label>
                    <div className="flex gap-2">
                      <Input
                        type="color"
                        value={brandingData.primary_color}
                        onChange={(e) => setBrandingData({...brandingData, primary_color: e.target.value})}
                        className="w-20"
                      />
                      <Input
                        value={brandingData.primary_color}
                        onChange={(e) => setBrandingData({...brandingData, primary_color: e.target.value})}
                        placeholder="#059669"
                      />
                    </div>
                  </div>
                </div>

                {/* Logo Upload */}
                <div>
                  <Label>Site Logo (Circular - will appear in navbar)</Label>
                  <div className="mt-2 flex items-center gap-4">
                    {brandingData.site_logo_url && (
                      <div className="w-16 h-16 rounded-full overflow-hidden bg-white border-2 border-slate-200">
                        <img src={brandingData.site_logo_url} alt="Logo" className="w-full h-full object-cover" />
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        if (e.target.files[0]) {
                          await handleBrandingImageUpload(e.target.files[0], 'site_logo_url');
                        }
                      }}
                      className="text-sm"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Recommended: Square image, at least 200x200px</p>
                </div>

                {/* Hero Section */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4">Homepage Hero Section</h3>
                  <div className="space-y-4">
                    <div>
                      <Label>Hero Title</Label>
                      <Input
                        value={brandingData.hero_title}
                        onChange={(e) => setBrandingData({...brandingData, hero_title: e.target.value})}
                        placeholder="A Pathway to Martial Excellence"
                      />
                    </div>
                    <div>
                      <Label>Hero Subtitle</Label>
                      <Textarea
                        value={brandingData.hero_subtitle}
                        onChange={(e) => setBrandingData({...brandingData, hero_subtitle: e.target.value})}
                        placeholder="Embark on a Journey of Cultural Enrichment"
                        rows={3}
                      />
                    </div>
                    <div>
                      <Label>Hero Image</Label>
                      <div className="mt-2 flex items-start gap-4">
                        {brandingData.hero_image_url && (
                          <img src={brandingData.hero_image_url} alt="Hero" className="w-32 h-24 object-cover rounded border" />
                        )}
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            if (e.target.files[0]) {
                              await handleBrandingImageUpload(e.target.files[0], 'hero_image_url');
                            }
                          }}
                          className="text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Additional Branding */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4">Additional Settings</h3>
                  <div className="space-y-4">
                    <div>
                      <Label>About Text</Label>
                      <Textarea
                        value={brandingData.about_text}
                        onChange={(e) => setBrandingData({...brandingData, about_text: e.target.value})}
                        placeholder="Tell your story..."
                        rows={4}
                      />
                    </div>
                    <div>
                      <Label>Footer Text</Label>
                      <Input
                        value={brandingData.footer_text}
                        onChange={(e) => setBrandingData({...brandingData, footer_text: e.target.value})}
                        placeholder="© 2024 Train In Japan. All rights reserved."
                      />
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-6 border-t">
                  <Button
                    onClick={handleBrandingUpdate}
                    disabled={uploadingBranding}
                    className="bg-emerald-700 hover:bg-emerald-800"
                  >
                    {uploadingBranding ? 'Saving...' : 'Save Branding Settings'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* MESSAGES TAB */}
          <TabsContent value="messages" className="mt-6">
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>Messages & Contact Submissions</h2>
              <p className="text-slate-600">View and manage all communications</p>
            </div>
            <MessagesPanel />
          </TabsContent>

        </Tabs>

      {/* Edit School Dialog */}
      <Dialog open={editSchoolDialog} onOpenChange={setEditSchoolDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit School Details & Branding</DialogTitle>
            <DialogDescription>Update your school information and branding</DialogDescription>
          </DialogHeader>
          
          {editingSchool && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>School Name</Label>
                  <Input value={editingSchool.name || ""} onChange={(e) => setEditingSchool({...editingSchool, name: e.target.value})} />
                </div>
                <div className="col-span-2">
                  <Label>Tagline</Label>
                  <Input value={editingSchool.tagline || ""} onChange={(e) => setEditingSchool({...editingSchool, tagline: e.target.value})} />
                </div>
                <div className="col-span-2">
                  <Label>Bio</Label>
                  <Textarea value={editingSchool.bio || ""} onChange={(e) => setEditingSchool({...editingSchool, bio: e.target.value})} rows={4} />
                </div>
                <div>
                  <Label>Location</Label>
                  <Input value={editingSchool.location || ""} onChange={(e) => setEditingSchool({...editingSchool, location: e.target.value})} />
                </div>
                <div>
                  <Label>Contact Email</Label>
                  <Input value={editingSchool.contact_email || ""} onChange={(e) => setEditingSchool({...editingSchool, contact_email: e.target.value})} />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={editingSchool.contact_phone || ""} onChange={(e) => setEditingSchool({...editingSchool, contact_phone: e.target.value})} />
                </div>
                <div>
                  <Label>Website</Label>
                  <Input value={editingSchool.website || ""} onChange={(e) => setEditingSchool({...editingSchool, website: e.target.value})} />
                </div>
                <div className="col-span-2">
                  <Label>School Logo</Label>
                  <div className="flex items-center gap-4">
                    {editingSchool.logo_url && (
                      <img src={editingSchool.logo_url} alt="Logo" className="w-20 h-20 object-cover rounded border" />
                    )}
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        if (e.target.files[0]) {
                          try {
                            const formData = new FormData();
                            formData.append('file', e.target.files[0]);
                            const res = await axios.post(`${API}/upload/image`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
                            const imageUrl = res.data.url.replace('/uploads/', '/api/uploads/');
                            setEditingSchool({...editingSchool, logo_url: imageUrl});
                            toast.success('Logo uploaded!');
                          } catch (err) {
                            toast.error('Failed to upload logo');
                          }
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="col-span-2">
                  <Label>Banner Image</Label>
                  <div className="flex items-center gap-4">
                    {editingSchool.banner_url && (
                      <img src={editingSchool.banner_url} alt="Banner" className="w-32 h-20 object-cover rounded border" />
                    )}
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={async (e) => {
                        if (e.target.files[0]) {
                          try {
                            const formData = new FormData();
                            formData.append('file', e.target.files[0]);
                            const res = await axios.post(`${API}/upload/image`, formData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
                            const imageUrl = res.data.url.replace('/uploads/', '/api/uploads/');
                            setEditingSchool({...editingSchool, banner_url: imageUrl});
                            toast.success('Banner uploaded!');
                          } catch (err) {
                            toast.error('Failed to upload banner');
                          }
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={() => { setEditSchoolDialog(false); setEditingSchool(null); }}>Cancel</Button>
                <Button onClick={async () => { try { await axios.put(`${API}/admin/schools/${editingSchool.id}/edit`, editingSchool, { withCredentials: true }); setEditSchoolDialog(false); setEditingSchool(null); fetchAdminSchoolData(); alert("School updated!"); } catch(e) { alert("Error: " + e.message); } }} className="bg-emerald-700 hover:bg-emerald-800">Save</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Program Dialog - For All Programs Tab */}
      <Dialog open={editProgramDialog} onOpenChange={setEditProgramDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Program</DialogTitle>
            <DialogDescription>Update program details. Changes will be saved immediately.</DialogDescription>
          </DialogHeader>
          
          {editingProgram && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label>Title</Label>
                  <Input 
                    value={editProgramForm.title} 
                    onChange={(e) => setEditProgramForm({...editProgramForm, title: e.target.value})}
                    placeholder="Program title"
                  />
                </div>
                
                <div className="md:col-span-2">
                  <Label>Description</Label>
                  <Textarea 
                    value={editProgramForm.description} 
                    onChange={(e) => setEditProgramForm({...editProgramForm, description: e.target.value})}
                    placeholder="Program description"
                    rows={3}
                  />
                </div>

                <div>
                  <Label>Category</Label>
                  <Select value={editProgramForm.category} onValueChange={(v) => setEditProgramForm({...editProgramForm, category: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Martial Arts">Martial Arts</SelectItem>
                      <SelectItem value="Cultural Arts">Cultural Arts</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Experience Level</Label>
                  <Select value={editProgramForm.experience_level} onValueChange={(v) => setEditProgramForm({...editProgramForm, experience_level: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="beginner">Beginner</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="advanced">Advanced</SelectItem>
                      <SelectItem value="all">All Levels</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Price (¥)</Label>
                  <Input 
                    type="number"
                    value={editProgramForm.price} 
                    onChange={(e) => setEditProgramForm({...editProgramForm, price: e.target.value})}
                    placeholder="Price in JPY"
                  />
                </div>

                <div>
                  <Label>Capacity</Label>
                  <Input 
                    type="number"
                    value={editProgramForm.capacity} 
                    onChange={(e) => setEditProgramForm({...editProgramForm, capacity: e.target.value})}
                    placeholder="Max students"
                  />
                </div>

                <div>
                  <Label>Start Date</Label>
                  <Input 
                    type="date"
                    value={editProgramForm.start_date} 
                    onChange={(e) => setEditProgramForm({...editProgramForm, start_date: e.target.value})}
                  />
                </div>

                <div>
                  <Label>End Date</Label>
                  <Input 
                    type="date"
                    value={editProgramForm.end_date} 
                    onChange={(e) => setEditProgramForm({...editProgramForm, end_date: e.target.value})}
                  />
                </div>

                <div className="md:col-span-2">
                  <Label>Image URL</Label>
                  <Input 
                    value={editProgramForm.image_url} 
                    onChange={(e) => setEditProgramForm({...editProgramForm, image_url: e.target.value})}
                    placeholder="https://..."
                  />
                  {editProgramForm.image_url && (
                    <img src={editProgramForm.image_url} alt="Preview" className="mt-2 w-full h-32 object-cover rounded" />
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditProgramDialog(false)}>Cancel</Button>
                <Button onClick={handleSaveEditedProgram} className="bg-emerald-600 hover:bg-emerald-700">
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </div>
  );
};

export default AdminDashboard;
